import React, { useState, useEffect, useRef, useMemo } from 'react';
import { UserSession } from '../types';
import { readDisplaySync, SAVED_DISPLAY_WARNING } from '../savedDisplaySync';
import { defaultSoapTitle } from '../utils/clinicalDocDisplay';
import { isCurrentCanonicalGoalStatus } from '../utils/canonicalGoalDirect';
import { signatureCanvasPoint } from '../utils/signatureCanvasPoint';
import {
  Play,
  RotateCcw,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ArrowLeft,
  FileText,
  UserCheck,
  Calendar,
  PenTool,
  FileCheck,
  Loader2,
  Target,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  Check,
  Trash2,
  X,
  Search
} from 'lucide-react';
import { StartSpeechButton } from './StartSpeechButton';
import { playPulseSeal } from '../utils/pulseAudio';
import {
  formatPartialSoapSaveError,
  interpretSoapSigningResponses,
  resolveSavedServiceTreatingClinician,
  soapConfirmationMessage
} from '../utils/mobileSoapSubmitResult';
import { assignmentRemoteSoapApproved, mobileSoapRequiresInPersonPad } from '../utils/teletherapyEsign';
import { inPersonEncounterAcknowledgment } from '../utils/soapEncounterAcknowledgment';
import {
  documentedDurationSecondsFromDraft,
  draftHasRecoverableEncounter,
  shouldDiscardStoredSoapDraft,
  stableSubmittedServiceId
} from '../utils/soapDraftRecovery';
import { compareSoapGoalPerformance } from '../utils/soapGoalPerformance';

interface PlannedSoapVisit {
  serviceId?: string;
  clientId?: string;
  dos?: string;
  startTime?: string;
  serviceCode?: string;
}

interface MobileSoapSessionViewProps {
  user: UserSession;
  preselectedClientId?: string | null;
  plannedVisit?: PlannedSoapVisit | null;
  onClearPreselectedClient?: () => void;
  onClearPlannedVisit?: () => void;
  onNavigateTab?: (tab: string) => void;
  onOpenAIWithPrompt?: (prompt: string) => void;
}

interface AssignedClient {
  client_id: string;
  client_first_name: string;
  client_last_name: string;
  ahcccs_id?: string;
  client_dob?: string;
  client_status?: string;
  auth_service_code?: string;
  auth_frequency?: string;
  active_auth_count?: number;
  remaining_auth_units?: number | null;
  auth_resolution?: 'unique' | 'none' | 'multiple' | 'zero';
  expected_frequency?: string;
  expected_duration_minutes?: number;
  provider_id: string;
  provider_name?: string;
  provider_role?: string;
  guardian_name?: string;
  guardian_relationship?: string;
  manual_soap_approved?: number | boolean | string | null;
}

function caseloadUnitsLabel(client: AssignedClient): string | null {
  if (client.auth_resolution === 'unique' && typeof client.remaining_auth_units === 'number') {
    return `${client.remaining_auth_units} auth units left`;
  }
  if (client.auth_resolution === 'zero') return '0 auth units left';
  return null;
}

function caseloadShowsNoActiveAuth(client: AssignedClient): boolean {
  if (client.auth_resolution) return client.auth_resolution === 'none';
  return !client.active_auth_count || client.active_auth_count <= 0;
}

function caseloadNeedsAuthReview(client: AssignedClient): boolean {
  return client.auth_resolution === 'multiple';
}

interface CanonicalProvider {
  id: string;
  name: string;
  role: string;
  title?: string;
  npi?: string;
  license_number?: string;
}

interface CanonicalGoal {
  id: string;
  description: string;
  baseline_performance?: string;
  target_percentage?: number;
  current_percentage?: number;
  status: string;
}

interface ClientAccentTheme {
  name: string;
  leftBorder: string;
  avatarBg: string;
  avatarGlow: string;
  rowHalo: string;
  startBtn: string;
  zeroPill: string;
}

const CLIENT_ACCENT_PALETTE: ClientAccentTheme[] = [
  // 1. Restrained Amber / Gold
  {
    name: 'amber',
    leftBorder: 'border-l-amber-500/75',
    avatarBg: 'bg-amber-500/15 text-amber-300 border border-amber-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(245,158,11,0.15)]',
    rowHalo: 'hover:border-amber-500/30',
    startBtn: '',
    zeroPill: 'bg-amber-950/40 text-amber-300/90 border-amber-500/30'
  },
  // 2. Deep Teal / Emerald
  {
    name: 'teal',
    leftBorder: 'border-l-teal-500/75',
    avatarBg: 'bg-teal-500/15 text-teal-300 border border-teal-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(20,184,166,0.15)]',
    rowHalo: 'hover:border-teal-500/30',
    startBtn: '',
    zeroPill: 'bg-teal-950/40 text-teal-300/90 border-teal-500/30'
  },
  // 3. Indigo / Violet
  {
    name: 'indigo',
    leftBorder: 'border-l-indigo-500/75',
    avatarBg: 'bg-indigo-500/15 text-indigo-300 border border-indigo-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(99,102,241,0.15)]',
    rowHalo: 'hover:border-indigo-500/30',
    startBtn: '',
    zeroPill: 'bg-indigo-950/40 text-indigo-300/90 border-indigo-500/30'
  },
  // 4. Cool Cyan / Steel-Blue
  {
    name: 'steel-blue',
    leftBorder: 'border-l-sky-500/75',
    avatarBg: 'bg-sky-500/15 text-sky-300 border border-sky-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(14,165,233,0.15)]',
    rowHalo: 'hover:border-sky-500/30',
    startBtn: '',
    zeroPill: 'bg-sky-950/40 text-sky-300/90 border-sky-500/30'
  },
  // 5. Restrained Gold (Amber / Gold family)
  {
    name: 'gold',
    leftBorder: 'border-l-yellow-600/75',
    avatarBg: 'bg-yellow-500/15 text-yellow-300 border border-yellow-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(234,179,8,0.15)]',
    rowHalo: 'hover:border-yellow-600/30',
    startBtn: '',
    zeroPill: 'bg-yellow-950/40 text-yellow-300/90 border-yellow-500/30'
  },
  // 6. Deep Emerald (Teal / Emerald family)
  {
    name: 'emerald',
    leftBorder: 'border-l-emerald-600/75',
    avatarBg: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(16,185,129,0.15)]',
    rowHalo: 'hover:border-emerald-500/30',
    startBtn: '',
    zeroPill: 'bg-emerald-950/40 text-emerald-300/90 border-emerald-500/30'
  },
  // 7. Muted Violet (Indigo / Violet family)
  {
    name: 'violet',
    leftBorder: 'border-l-violet-500/75',
    avatarBg: 'bg-violet-500/15 text-violet-300 border border-violet-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(139,92,246,0.15)]',
    rowHalo: 'hover:border-violet-500/30',
    startBtn: '',
    zeroPill: 'bg-violet-950/40 text-violet-300/90 border-violet-500/30'
  },
  // 8. Steel-Blue (Cool Cyan / Steel-Blue family)
  {
    name: 'slate-cyan',
    leftBorder: 'border-l-cyan-600/75',
    avatarBg: 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/35',
    avatarGlow: 'shadow-[0_0_8px_rgba(6,182,212,0.15)]',
    rowHalo: 'hover:border-cyan-500/30',
    startBtn: '',
    zeroPill: 'bg-cyan-950/40 text-cyan-300/90 border-cyan-500/30'
  }
];

interface TargetedGoalProgress {
  targeted: boolean;
  accuracy: number;
  cueing: string;
  evidenceNote: string;
}

interface EligibleMissedService {
  id: string;
  dos: string;
  original_dos?: string;
  service_code?: string;
  status?: string;
  cancellation_reason?: string;
  provider_id?: string;
  provider_name?: string;
  start_time?: string;
  end_time?: string;
  duration_minutes?: number;
}

const CUEING_OPTIONS = [
  'Independent',
  'Min Verbal',
  'Mod Visual',
  'Max Tactile',
  'Hand-over-Hand'
];

const ACCURACY_PRESETS = [50, 60, 70, 80, 90, 100];

// Canonical identity and active session storage helpers
function getCanonicalProviderId(user: UserSession): string {
  return user.providerId || (user as any).provider_id || user.id;
}

function getSessionStorageKeys(user: UserSession): { primaryKey: string; allKeys: string[] } {
  const provId = getCanonicalProviderId(user);
  const primaryKey = `allegiant_active_mobile_session_prov_${provId}`;
  const keys = [
    primaryKey,
    `allegiant_active_mobile_session_${provId}`,
    `allegiant_active_mobile_session_${user.id}`,
    'allegiant_active_mobile_session_current'
  ];
  const allKeys = Array.from(new Set(keys));
  return { primaryKey, allKeys };
}

type ClientsTreatedTogetherSelection = 1 | 2 | 3 | '';
type ServiceSettingSelection = 'CLINIC' | 'HOME' | 'TELETHERAPY' | '';
type TelePatientLocationSelection = 'HOME' | 'ELSEWHERE' | '';

function parseStoredClientsTreatedTogether(raw: unknown): ClientsTreatedTogetherSelection {
  if (raw === 1 || raw === 2 || raw === 3) return raw;
  if (raw === '1' || raw === '2' || raw === '3') return Number(raw) as 1 | 2 | 3;
  return '';
}

function parseStoredServiceSetting(raw: unknown): ServiceSettingSelection {
  const value = String(raw || '').trim().toUpperCase();
  if (value === 'CLINIC' || value === 'HOME' || value === 'TELETHERAPY') return value;
  return '';
}

function parseStoredTelePatientLocation(raw: unknown): TelePatientLocationSelection {
  const value = String(raw || '').trim().toUpperCase();
  if (value === 'HOME' || value === 'ELSEWHERE') return value;
  return '';
}

type GoalsLoadStatus = 'pending' | 'ready' | 'failed';
type GoalsLoadState = { clientId: string; status: GoalsLoadStatus };

function goalsLoadForClient(state: GoalsLoadState | null, clientId: string | null | undefined): GoalsLoadStatus {
  const id = String(clientId || '').trim();
  if (!id || !state || state.clientId !== id) return 'pending';
  return state.status;
}

function placeOfServiceFromMobileSoapSetting(
  setting: ServiceSettingSelection,
  telePatientLocation: TelePatientLocationSelection
): '11' | '12' | '02' | '10' | '' {
  if (setting === 'CLINIC') return '11';
  if (setting === 'HOME') return '12';
  if (setting === 'TELETHERAPY' && telePatientLocation === 'HOME') return '10';
  if (setting === 'TELETHERAPY' && telePatientLocation === 'ELSEWHERE') return '02';
  return '';
}

function loadActiveSessionFromStorage(user: UserSession): { data: any; key: string } | null {
  try {
    const provId = getCanonicalProviderId(user);
    const { allKeys } = getSessionStorageKeys(user);
    for (const key of allKeys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const recoverable = draftHasRecoverableEncounter(parsed);
      if (
        parsed &&
        parsed.selectedClient &&
        (recoverable || (parsed.currentView !== 'success' && !parsed.submitted))
      ) {
        // Storage safety check: Validate stored provider matches authenticated canonical provider
        const storedProvId = parsed.providerId || parsed.selectedClient?.provider_id;
        if (storedProvId && storedProvId !== provId) {
          // Never restore mismatched provider session across clinician logins
          continue;
        }
        if (key === 'allegiant_active_mobile_session_current' && (!storedProvId || storedProvId !== provId)) {
          // Require explicit provider match for global fallback key
          continue;
        }

        const now = Date.now();
        const lastActive = parsed.lastActiveTimestamp || parsed.startTimestamp || 0;
        const isRunning = parsed.timerState === 'RUNNING' || Boolean(parsed.isTimerRunning);
        const isOldInactive = !isRunning && (now - lastActive > 20 * 60 * 60 * 1000);
        if (!isOldInactive || recoverable) {
          return { data: parsed, key };
        }
      }
    }
  } catch (err) {
    console.warn('Could not read active mobile session from storage:', err);
  }
  return null;
}

function saveActiveSessionToStorage(user: UserSession, state: Record<string, any>) {
  try {
    const provId = getCanonicalProviderId(user);
    const { primaryKey } = getSessionStorageKeys(user);
    const payload = {
      ...state,
      providerId: provId,
      userId: user.id
    };
    const serialized = JSON.stringify(payload);
    // Authoritative provider-scoped key
    localStorage.setItem(primaryKey, serialized);
    // Fallback global key validated by providerId on restore
    localStorage.setItem('allegiant_active_mobile_session_current', serialized);
    const legacyKey = `allegiant_active_mobile_session_${provId}`;
    if (legacyKey !== primaryKey) {
      localStorage.setItem(legacyKey, serialized);
    }
  } catch (err) {
    console.warn('Could not save active mobile session to storage:', err);
  }
}

function clearActiveSessionFromStorage(user: UserSession) {
  try {
    const provId = getCanonicalProviderId(user);
    const { allKeys } = getSessionStorageKeys(user);
    for (const key of allKeys) {
      if (key === 'allegiant_active_mobile_session_current') {
        const raw = localStorage.getItem(key);
        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const storedProvId = parsed.providerId || parsed.selectedClient?.provider_id;
            if (!storedProvId || storedProvId === provId) {
              localStorage.removeItem(key);
            }
          } catch {
            localStorage.removeItem(key);
          }
        }
      } else {
        localStorage.removeItem(key);
      }
    }
  } catch (err) {
    console.warn('Could not clear active mobile session from storage:', err);
  }
}

export const MobileSoapSessionView: React.FC<MobileSoapSessionViewProps> = ({
  user,
  preselectedClientId,
  plannedVisit,
  onClearPreselectedClient,
  onClearPlannedVisit,
  onNavigateTab,
  onOpenAIWithPrompt
}) => {
  // Synchronously restore any active in-progress encounter owned by this provider identity
  const initialSessionData = useMemo(() => {
    return loadActiveSessionFromStorage(user)?.data || null;
  }, [user.id, user.providerId]);

  // Clinical roles permitted to conduct or resume speech treatment sessions
  const isPermittedToResume = ['OWNER', 'SUPERVISOR', 'ASSISTANT'].includes(user.role);

  // View state: 'caseload' | 'session' | 'success'
  const [currentView, setCurrentView] = useState<'caseload' | 'session' | 'success'>(() => {
    return initialSessionData?.currentView === 'session' ? 'session' : 'caseload';
  });

  // Caseload list & services for weekly tracking
  const [assignments, setAssignments] = useState<AssignedClient[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [providers, setProviders] = useState<CanonicalProvider[]>([]);
  const [loadingCaseload, setLoadingCaseload] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Active Session state
  const [selectedClient, setSelectedClient] = useState<AssignedClient | null>(() => {
    return initialSessionData?.selectedClient || null;
  });
  const [encounterId, setEncounterId] = useState<string>(() => {
    return initialSessionData?.encounterId || '';
  });
  const [persistedServiceId, setPersistedServiceId] = useState<string>(() => {
    return initialSessionData?.persistedServiceId || '';
  });
  const [persistedDocId, setPersistedDocId] = useState<string>(() => {
    return initialSessionData?.persistedDocId || '';
  });
  const [plannedServiceId, setPlannedServiceId] = useState<string>(() => {
    return initialSessionData?.plannedServiceId || '';
  });
  const [plannedServiceCode, setPlannedServiceCode] = useState<string>(() => {
    return initialSessionData?.plannedServiceCode || '';
  });
  const [submissionStarted, setSubmissionStarted] = useState<boolean>(() => {
    return Boolean(initialSessionData?.submissionStarted);
  });
  const [recoveryNotice, setRecoveryNotice] = useState<string>('');
  const [encounterProviderId, setEncounterProviderId] = useState<string>(() => {
    return initialSessionData?.encounterProviderId || initialSessionData?.providerId || initialSessionData?.selectedClient?.provider_id || '';
  });
  const [clientGoals, setClientGoals] = useState<CanonicalGoal[]>([]);
  const [loadingGoals, setLoadingGoals] = useState<boolean>(false);
  const [goalsLoadState, setGoalsLoadState] = useState<GoalsLoadState | null>(null);
  const goalsRequestClientIdRef = useRef<string>('');

  // Authorization override state
  const [isAuthOverride, setIsAuthOverride] = useState<boolean>(() => {
    return Boolean(initialSessionData?.isAuthOverride);
  });
  const [authOverrideReason, setAuthOverrideReason] = useState<string>(() => {
    return initialSessionData?.authOverrideReason || '';
  });

  // Timestamps & Timer
  const [dos, setDos] = useState<string>(() => {
    return initialSessionData?.dos || new Date().toISOString().split('T')[0];
  });
  const [startTimeStr, setStartTimeStr] = useState<string>(() => {
    return initialSessionData?.startTimeStr || '';
  });
  const [endTimeStr, setEndTimeStr] = useState<string>(() => {
    return initialSessionData?.endTimeStr || '';
  });
  const [startTimestamp, setStartTimestamp] = useState<number>(() => {
    return initialSessionData?.startTimestamp || 0;
  });
  const [lastActiveTimestamp, setLastActiveTimestamp] = useState<number>(() => {
    return initialSessionData?.lastActiveTimestamp || 0;
  });
  const [accumulatedActiveSeconds, setAccumulatedActiveSeconds] = useState<number>(() => {
    return documentedDurationSecondsFromDraft(initialSessionData);
  });
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(() => {
    return documentedDurationSecondsFromDraft(initialSessionData);
  });

  // Authoritative provider identity: Canonical chain
  // active session / service encounter provider_id -> canonical provider record -> clinician name / credential
  const effectiveEncounterProvId = encounterProviderId || selectedClient?.provider_id || '';
  const canonicalEncounterProvider = providers.find((p) => p.id === effectiveEncounterProvId);
  const isEncounterProviderResolved = Boolean(canonicalEncounterProvider);
  const canonicalClinicianName = canonicalEncounterProvider?.name || '';
  const isEncounterSlpa = Boolean(
    canonicalEncounterProvider && (
      canonicalEncounterProvider.role === 'SLPA' ||
      canonicalEncounterProvider.role === 'ASSISTANT' ||
      /slpa|assistant/i.test(canonicalEncounterProvider.title || '') ||
      /slpa|assistant/i.test(canonicalEncounterProvider.name || '')
    )
  );

  const currentUserProvId = getCanonicalProviderId(user);
  const isTreatingProviderMismatch = Boolean(
    isEncounterProviderResolved && currentUserProvId && currentUserProvId !== canonicalEncounterProvider?.id
  );

  // Goal performance map: goalId -> TargetedGoalProgress
  const [goalProgressMap, setGoalProgressMap] = useState<{ [goalId: string]: TargetedGoalProgress }>(() => {
    return initialSessionData?.goalProgressMap || {};
  });

  // Clinician session observation & SOAP Narrative
  const [clinicianEvidence, setClinicianEvidence] = useState<string>(() => {
    return initialSessionData?.clinicianEvidence || '';
  });
  const [sessionNarrative, setSessionNarrative] = useState<string>(() => {
    return initialSessionData?.sessionNarrative || '';
  });
  const [subjective, setSubjective] = useState<string>(() => {
    return initialSessionData?.subjective || '';
  });
  const [objective, setObjective] = useState<string>(() => {
    return initialSessionData?.objective || '';
  });
  const [assessment, setAssessment] = useState<string>(() => {
    return initialSessionData?.assessment || '';
  });
  const [plan, setPlan] = useState<string>(() => {
    return initialSessionData?.plan || '';
  });
  const [showSoapReview, setShowSoapReview] = useState<boolean>(() => {
    return Boolean(initialSessionData?.showSoapReview || initialSessionData?.subjective || initialSessionData?.assessment || initialSessionData?.plan);
  });
  const [medicalNecessity, setMedicalNecessity] = useState<string>(() => {
    return initialSessionData?.medicalNecessity || 'Skilled speech-language pathology intervention is medically necessary to remediate functional speech, language, and communication deficits impacting daily life activities and prevent developmental regression.';
  });

  // AI Drafting state

  // Guardian Signature
  const [guardianName, setGuardianName] = useState<string>(() => {
    return initialSessionData?.guardianName || '';
  });
  const [guardianRelationship, setGuardianRelationship] = useState<string>(() => {
    return initialSessionData?.guardianRelationship || 'Parent / Legal Guardian';
  });
  const [guardianSignatureData, setGuardianSignatureData] = useState<string>(() => {
    return initialSessionData?.guardianSignatureData || '';
  });
  const [guardianSignatureChoice, setGuardianSignatureChoice] = useState<'IN_PERSON' | 'REMOTE'>(() => {
    return initialSessionData?.guardianSignatureChoice === 'REMOTE' ? 'REMOTE' : 'IN_PERSON';
  });
  const hasGuardianSignatureEvidence = Boolean(
    guardianName.trim() &&
    guardianRelationship.trim() &&
    guardianSignatureData.startsWith('data:image/') &&
    guardianSignatureData.length > 200
  );
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isDrawing, setIsDrawing] = useState<boolean>(false);

  // Restore canvas signature bitmap when returning to view or when signature data is loaded
  useEffect(() => {
    if (canvasRef.current && guardianSignatureData) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
        };
        img.src = guardianSignatureData;
      }
    }
  }, [guardianSignatureData, currentView]);

  // Active Session Navigation & Discard Safety
  const [showDiscardModal, setShowDiscardModal] = useState<boolean>(false);
  const [conflictClient, setConflictClient] = useState<AssignedClient | null>(null);
  const [aboveFrequencyWarningClient, setAboveFrequencyWarningClient] = useState<AssignedClient | null>(null);
  const [noAuthWarningClient, setNoAuthWarningClient] = useState<AssignedClient | null>(null);

  // Submitting state
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccessResult, setSubmitSuccessResult] = useState<{
    docId: string;
    status: string;
    serviceId?: string;
    treatingClinician: string;
    isSlpa: boolean;
    kind: 'finalized' | 'pending_supervision' | 'pending_signatures';
    awaitingRemoteGuardian: boolean;
  } | null>(null);

  // Operational Required-Field Validation state (no browser alert/confirm)
  const [validationNotice, setValidationNotice] = useState<{
    field: 'context' | 'goals' | 'narrative' | 'objective' | 'guardian' | 'attestation' | 'makeup' | 'setting' | 'clients_treated_together';
    message: string;
  } | null>(null);
  const [highlightedField, setHighlightedField] = useState<string | null>(null);
  const [clinicianAttestationChecked, setClinicianAttestationChecked] = useState<boolean>(() => {
    return initialSessionData?.clinicianAttestationChecked !== undefined
      ? Boolean(initialSessionData.clinicianAttestationChecked)
      : true;
  });

  // Makeup Visit state
  const [eligibleMissedVisits, setEligibleMissedVisits] = useState<EligibleMissedService[]>([]);
  const [loadingEligibleMissed, setLoadingEligibleMissed] = useState<boolean>(false);
  const [isMakeupVisit, setIsMakeupVisit] = useState<boolean>(false);
  const [selectedMissedServiceId, setSelectedMissedServiceId] = useState<string>('');
  const [clientsTreatedTogether, setClientsTreatedTogether] = useState<ClientsTreatedTogetherSelection>(() => {
    return parseStoredClientsTreatedTogether(initialSessionData?.clientsTreatedTogether);
  });
  const [serviceSetting, setServiceSetting] = useState<ServiceSettingSelection>(() => {
    return parseStoredServiceSetting(initialSessionData?.serviceSetting);
  });
  const [telePatientLocation, setTelePatientLocation] = useState<TelePatientLocationSelection>(() => {
    return parseStoredTelePatientLocation(initialSessionData?.telePatientLocation);
  });
  const approvedRemoteAssignment = assignmentRemoteSoapApproved(selectedClient?.manual_soap_approved);
  const requiresOnsiteGuardianPad = mobileSoapRequiresInPersonPad({
    setting: serviceSetting,
    assignmentApproved: selectedClient?.manual_soap_approved,
    signatureChoice: guardianSignatureChoice
  });

  const formatMissedDos = (dStr: string) => {
    if (!dStr) return '';
    const [year, month, day] = dStr.split('-');
    if (!year || !month || !day) return dStr;
    const d = new Date(Number(year), Number(month) - 1, Number(day));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const triggerValidationError = (
    field: 'context' | 'goals' | 'narrative' | 'objective' | 'guardian' | 'attestation' | 'makeup' | 'duration' | 'setting' | 'clients_treated_together',
    message: string,
    elementId: string
  ) => {
    setValidationNotice({ field, message });
    setHighlightedField(field);

    const el = document.getElementById(elementId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const interactiveEl = el.querySelector('input, textarea, select') as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (interactiveEl) {
        setTimeout(() => interactiveEl.focus(), 250);
      }
    }

    setTimeout(() => {
      setHighlightedField((curr) => (curr === field ? null : curr));
    }, 4500);
  };

  // 1. Fetch caseload assignments and weekly services
  const fetchCaseload = async () => {
    setLoadingCaseload(true);
    try {
      const assignUrl = user.role === 'SUPERVISOR'
        ? '/api/assignments?activeOnly=1&treatingOnly=1'
        : '/api/assignments?activeOnly=1';
      const [assignRes, svcRes, provRes] = await Promise.all([
        fetch(assignUrl),
        fetch('/api/services'),
        fetch('/api/providers')
      ]);

      if (assignRes.ok) {
        const text = await assignRes.text();
        try {
          const assignData = JSON.parse(text);
          if (Array.isArray(assignData)) {
            setAssignments(assignData);
          }
        } catch {
          console.error('API /api/assignments returned non-JSON response:', text.slice(0, 120));
        }
      }
      if (svcRes.ok) {
        const text = await svcRes.text();
        try {
          const svcData = JSON.parse(text);
          if (Array.isArray(svcData)) {
            setServices(svcData);
          }
        } catch {
          console.error('API /api/services returned non-JSON response:', text.slice(0, 120));
        }
      }
      if (provRes && provRes.ok) {
        try {
          const provData = await provRes.json();
          if (Array.isArray(provData)) {
            setProviders(provData);
          }
        } catch {
          console.error('API /api/providers returned non-JSON response');
        }
      }
    } catch (err) {
      console.error('Failed to load caseload:', err);
    } finally {
      setLoadingCaseload(false);
    }
  };

  useEffect(() => {
    fetchCaseload();
  }, [user.id, user.role]);

  // 2. Check for restored in-progress session from localStorage with stale session verification
  useEffect(() => {
    const validateAndRestoreSession = async () => {
      try {
        const stored = loadActiveSessionFromStorage(user);
        if (!stored || !stored.data) return;
        const parsed = stored.data;

        if (shouldDiscardStoredSoapDraft(parsed, Date.now())) {
          clearActiveSessionFromStorage(user);
          return;
        }

        const sessionDos = parsed.dos || new Date().toISOString().split('T')[0];
        const now = Date.now();
        const lastActive = parsed.lastActiveTimestamp || parsed.startTimestamp || 0;
        const isRunning = parsed.timerState === 'RUNNING' || Boolean(parsed.isTimerRunning);
        const isOldInactiveSession = !isRunning && (now - lastActive > 20 * 60 * 60 * 1000);

        if (isOldInactiveSession && !draftHasRecoverableEncounter(parsed)) {
          clearActiveSessionFromStorage(user);
          return;
        }

        // If this draft was already explicitly submitted (has a submittedDocId), verify whether it was recorded on the server
        if (parsed.submittedDocId && !draftHasRecoverableEncounter(parsed)) {
          try {
            const checkRes = await fetch(
              `/api/clinical/docs?client_id=${parsed.selectedClient.client_id}&doc_type=SOAP&startDate=${sessionDos}&endDate=${sessionDos}`
            );
            if (checkRes.ok) {
              const existingDocs = await checkRes.json();
              if (Array.isArray(existingDocs) && existingDocs.length > 0) {
                const isAlreadySubmitted = existingDocs.some((d: any) => d.id === parsed.submittedDocId);
                if (isAlreadySubmitted) {
                  clearActiveSessionFromStorage(user);
                  setCurrentView('caseload');
                  setSelectedClient(null);
                  return;
                }
              }
            }
          } catch (checkErr) {
            console.warn('Could not verify note status against server on load:', checkErr);
          }
        }

        setSelectedClient(parsed.selectedClient);
        if (parsed.encounterId) {
          setEncounterId(parsed.encounterId);
        }
        if (parsed.persistedServiceId) {
          setPersistedServiceId(parsed.persistedServiceId);
        }
        if (parsed.persistedDocId) {
          setPersistedDocId(parsed.persistedDocId);
        }
        if (parsed.plannedServiceId) {
          setPlannedServiceId(parsed.plannedServiceId);
        }
        if (parsed.plannedServiceCode) {
          setPlannedServiceCode(parsed.plannedServiceCode);
        }
        if (parsed.submissionStarted) {
          setSubmissionStarted(true);
        }
        setIsAuthOverride(Boolean(parsed.isAuthOverride));
        setAuthOverrideReason(parsed.authOverrideReason || '');
        setDos(sessionDos);
        setStartTimeStr(parsed.startTimeStr || '');
        setEndTimeStr(parsed.endTimeStr || '');
        setStartTimestamp(parsed.startTimestamp || Date.now());
        setGoalProgressMap(parsed.goalProgressMap || {});
        setClinicianEvidence(parsed.clinicianEvidence || '');
        setSessionNarrative(parsed.sessionNarrative || '');
        setSubjective(parsed.subjective || '');
        setObjective(parsed.objective || '');
        setAssessment(parsed.assessment || '');
        if (parsed.encounterProviderId || parsed.providerId || parsed.selectedClient?.provider_id) {
          setEncounterProviderId(parsed.encounterProviderId || parsed.providerId || parsed.selectedClient?.provider_id);
        }
        setPlan(parsed.plan || '');
        if (parsed.showSoapReview || parsed.subjective || parsed.assessment || parsed.plan) {
          setShowSoapReview(true);
        }
        if (parsed.medicalNecessity) setMedicalNecessity(parsed.medicalNecessity);
        setGuardianName(parsed.guardianName || '');
        setGuardianRelationship(parsed.guardianRelationship || 'Parent / Legal Guardian');
        setGuardianSignatureData(parsed.guardianSignatureData || '');
        setGuardianSignatureChoice(parsed.guardianSignatureChoice === 'REMOTE' ? 'REMOTE' : 'IN_PERSON');
        if (parsed.clinicianAttestationChecked !== undefined) {
          setClinicianAttestationChecked(Boolean(parsed.clinicianAttestationChecked));
        }
        if (parsed.isMakeupVisit) {
          setIsMakeupVisit(true);
          setSelectedMissedServiceId(parsed.selectedMissedServiceId || '');
        }
        setClientsTreatedTogether(parseStoredClientsTreatedTogether(parsed.clientsTreatedTogether));
        setServiceSetting(parseStoredServiceSetting(parsed.serviceSetting));
        setTelePatientLocation(parseStoredTelePatientLocation(parsed.telePatientLocation));

        const savedDuration = documentedDurationSecondsFromDraft(parsed);
        setElapsedSeconds(savedDuration);
        setAccumulatedActiveSeconds(savedDuration);
        setLastActiveTimestamp(parsed.lastActiveTimestamp || parsed.startTimestamp || 0);

        if (parsed.currentView === 'caseload') {
          setCurrentView('caseload');
        } else {
          setCurrentView('session');
        }
        // Load goals for this client
        fetchClientGoals(parsed.selectedClient.client_id);
      } catch (e) {
        console.warn('Could not restore cached mobile session:', e);
        clearActiveSessionFromStorage(user);
      }
    };

    validateAndRestoreSession();
  }, [user.id, user.role, user.providerId]);

  // 3. Handle preselectedClientId if passed from outside
  useEffect(() => {
    if (preselectedClientId && assignments.length > 0 && currentView === 'caseload' && !submitSuccessResult) {
      const match = assignments.find((a) => a.client_id === preselectedClientId);
      if (match) {
        if (selectedClient && selectedClient.client_id === match.client_id) {
          handleStartSession(match);
        } else if (selectedClient) {
          setConflictClient(match);
        } else {
          handleStartSession(match);
        }
      }
      if (onClearPreselectedClient) {
        onClearPreselectedClient();
      }
    }
  }, [preselectedClientId, assignments]);

  // Scroll to top whenever entering active session so encounter header is fully visible without clipping
  useEffect(() => {
    if (currentView === 'session') {
      window.scrollTo({ top: 0, behavior: 'instant' });
      const mainEl = document.querySelector('main');
      if (mainEl) {
        mainEl.scrollTo({ top: 0, behavior: 'instant' });
      }
    }
  }, [currentView]);

  // Keep current state accessible to unmount / beforeunload listeners
  const selectedClientRef = useRef(selectedClient);
  selectedClientRef.current = selectedClient;
  const encounterProviderIdRef = useRef(encounterProviderId);
  encounterProviderIdRef.current = encounterProviderId;
  const lastActiveTimestampRef = useRef(lastActiveTimestamp);
  lastActiveTimestampRef.current = lastActiveTimestamp;
  const accumulatedActiveSecondsRef = useRef(accumulatedActiveSeconds);
  accumulatedActiveSecondsRef.current = accumulatedActiveSeconds;
  const elapsedSecondsRef = useRef(elapsedSeconds);
  elapsedSecondsRef.current = elapsedSeconds;
  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;
  const isAuthOverrideRef = useRef(isAuthOverride);
  isAuthOverrideRef.current = isAuthOverride;
  const authOverrideReasonRef = useRef(authOverrideReason);
  authOverrideReasonRef.current = authOverrideReason;

  // Persist session before unmount or browser reload
  useEffect(() => {
    const handleUnloadOrUnmount = () => {
      if (selectedClientRef.current) {
        const preservedDuration = documentedDurationSecondsFromDraft({
          accumulatedActiveSeconds: accumulatedActiveSecondsRef.current,
          elapsedSeconds: elapsedSecondsRef.current
        });
        const preservedLastActive = lastActiveTimestampRef.current;

        saveActiveSessionToStorage(user, {
          selectedClient: selectedClientRef.current,
          encounterProviderId: encounterProviderIdRef.current,
          dos,
          startTimeStr,
          endTimeStr,
          startTimestamp,
          lastActiveTimestamp: preservedLastActive,
          accumulatedActiveSeconds: preservedDuration,
          elapsedSeconds: preservedDuration,
          timerState: 'PAUSED',
          isTimerRunning: false,
          lastSavedTimestamp: Date.now(),
          currentView: currentViewRef.current,
          isAuthOverride: isAuthOverrideRef.current,
          authOverrideReason: authOverrideReasonRef.current,
          goalProgressMap,
          clinicianEvidence,
          sessionNarrative,
          subjective,
          objective,
          assessment,
          plan,
          showSoapReview,
          medicalNecessity,
          guardianName,
          guardianRelationship,
          guardianSignatureData,
          guardianSignatureChoice,
          clinicianAttestationChecked,
          encounterId,
          persistedServiceId,
          persistedDocId,
          plannedServiceId,
          plannedServiceCode,
          submissionStarted,
          isMakeupVisit,
          selectedMissedServiceId,
          clientsTreatedTogether,
          serviceSetting,
          telePatientLocation
        });
      }
    };

    window.addEventListener('beforeunload', handleUnloadOrUnmount);
    return () => {
      window.removeEventListener('beforeunload', handleUnloadOrUnmount);
      handleUnloadOrUnmount();
    };
  }, [
    user,
    dos,
    startTimeStr,
    endTimeStr,
    startTimestamp,
    goalProgressMap,
    clinicianEvidence,
    sessionNarrative,
    subjective,
    objective,
    assessment,
    plan,
    showSoapReview,
    medicalNecessity,
    guardianName,
    guardianRelationship,
    guardianSignatureData,
    guardianSignatureChoice,
    clinicianAttestationChecked,
    encounterId,
    persistedServiceId,
    persistedDocId,
    plannedServiceId,
    plannedServiceCode,
    submissionStarted,
    isMakeupVisit,
    selectedMissedServiceId,
    clientsTreatedTogether,
    serviceSetting,
    telePatientLocation
  ]);

  // 5. Sync active session state to localStorage whenever active
  useEffect(() => {
    if (selectedClient && (currentView === 'session' || currentView === 'caseload')) {
      const stateToSave = {
        selectedClient,
        encounterProviderId,
        encounterId,
        persistedServiceId,
        persistedDocId,
        plannedServiceId,
        plannedServiceCode,
        submissionStarted,
        dos,
        startTimeStr,
        endTimeStr,
        startTimestamp,
        lastActiveTimestamp,
        accumulatedActiveSeconds,
        elapsedSeconds,
        timerState: 'PAUSED',
        isTimerRunning: false,
        lastSavedTimestamp: Date.now(),
        currentView,
        isAuthOverride,
        authOverrideReason,
        goalProgressMap,
        clinicianEvidence,
        sessionNarrative,
        subjective,
        objective,
        assessment,
        plan,
        showSoapReview,
        medicalNecessity,
        guardianName,
        guardianRelationship,
        guardianSignatureData,
        guardianSignatureChoice,
        clinicianAttestationChecked,
        isMakeupVisit,
        selectedMissedServiceId,
        clientsTreatedTogether,
        serviceSetting,
        telePatientLocation
      };
      saveActiveSessionToStorage(user, stateToSave);
    }
  }, [
    selectedClient,
    encounterProviderId,
    encounterId,
    persistedServiceId,
    persistedDocId,
    plannedServiceId,
    plannedServiceCode,
    submissionStarted,
    dos,
    startTimeStr,
    endTimeStr,
    startTimestamp,
    lastActiveTimestamp,
    accumulatedActiveSeconds,
    elapsedSeconds,
    currentView,
    isAuthOverride,
    authOverrideReason,
    goalProgressMap,
    clinicianEvidence,
    sessionNarrative,
    subjective,
    objective,
    assessment,
    plan,
    showSoapReview,
    medicalNecessity,
    guardianName,
    guardianRelationship,
    guardianSignatureData,
    guardianSignatureChoice,
    clinicianAttestationChecked,
    isMakeupVisit,
    selectedMissedServiceId,
    clientsTreatedTogether,
    serviceSetting,
    telePatientLocation,
    user
  ]);

  // 6. Minimal Shared Presence Heartbeat
  // Observability only: registers active presence, refreshes periodically, never contains clinical draft/narrative
  useEffect(() => {
    if (!selectedClient || currentView === 'success' || submitSuccessResult) {
      return;
    }

    const provId = getCanonicalProviderId(user);
    const clientId = selectedClient.client_id;
    const clientName = `${selectedClient.first_name} ${selectedClient.last_name}`;
    const serviceCode = selectedClient.auth_service_code || '92507';
    const authException = isMakeupVisit
      ? 'Makeup'
      : (isAuthOverride || caseloadShowsNoActiveAuth(selectedClient))
        ? 'No Active Auth'
        : null;

    const sendHeartbeat = () => {
      fetch('/api/sessions/presence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId,
          clientName,
          providerId: provId,
          providerName: canonicalClinicianName,
          startTimestamp: startTimestamp || Date.now(),
          serviceCode,
          isActive: true,
          authException
        })
      }).catch(() => {});
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 25000);
    return () => {
      clearInterval(interval);
    };
  }, [
    selectedClient?.client_id,
    user.id,
    user.providerId,
    canonicalClinicianName,
    startTimestamp,
    isMakeupVisit,
    isAuthOverride,
    selectedClient?.auth_resolution,
    selectedClient?.active_auth_count,
    currentView,
    submitSuccessResult
  ]);

  // Fetch eligible missed services for makeup visit linkage when client or DOS changes
  useEffect(() => {
    if (!selectedClient?.client_id || !dos) {
      setEligibleMissedVisits([]);
      setIsMakeupVisit(false);
      setSelectedMissedServiceId('');
      return;
    }

    let active = true;
    setLoadingEligibleMissed(true);

    fetch(`/api/clients/${selectedClient.client_id}/eligible-missed-services?dos=${dos}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch eligible missed visits');
        return res.json();
      })
      .then((data: any[]) => {
        if (active) {
          if (Array.isArray(data)) {
            setEligibleMissedVisits(data);
            if (data.length === 0) {
              setIsMakeupVisit(false);
              setSelectedMissedServiceId('');
            } else if (selectedMissedServiceId && !data.some((d) => d.id === selectedMissedServiceId)) {
              setSelectedMissedServiceId('');
            }
          } else {
            setEligibleMissedVisits([]);
            setIsMakeupVisit(false);
            setSelectedMissedServiceId('');
          }
        }
      })
      .catch((err) => {
        console.warn('Error fetching eligible missed services:', err);
        if (active) {
          setEligibleMissedVisits([]);
          setIsMakeupVisit(false);
          setSelectedMissedServiceId('');
        }
      })
      .finally(() => {
        if (active) setLoadingEligibleMissed(false);
      });

    return () => {
      active = false;
    };
  }, [selectedClient?.client_id, dos]);

  // Load canonical goals for selected client
  const fetchClientGoals = async (clientId: string) => {
    const requestedId = String(clientId || '').trim();
    if (!requestedId) return;
    goalsRequestClientIdRef.current = requestedId;
    setGoalsLoadState({ clientId: requestedId, status: 'pending' });
    setClientGoals([]);
    setLoadingGoals(true);
    try {
      const res = await fetch(`/api/clients/${requestedId}/goals`);
      if (goalsRequestClientIdRef.current !== requestedId) return;
      if (res.ok) {
        const data: CanonicalGoal[] = await res.json();
        setGoalProgressMap((prev) => {
          const visible = data.filter((g) =>
            isCurrentCanonicalGoalStatus(g.status) ||
            Boolean(prev[g.id]?.targeted)
          );
          setClientGoals(visible);
          const next = { ...prev };
          visible.forEach((g) => {
            if (!next[g.id]) {
              next[g.id] = {
                targeted: false,
                accuracy: g.target_percentage || 80,
                cueing: 'Mod Visual',
                evidenceNote: ''
              };
            }
          });
          return next;
        });
        setGoalsLoadState({ clientId: requestedId, status: 'ready' });
      } else {
        setGoalsLoadState({ clientId: requestedId, status: 'failed' });
      }
    } catch (err) {
      console.error('Failed to load goals:', err);
      if (goalsRequestClientIdRef.current !== requestedId) return;
      setGoalsLoadState({ clientId: requestedId, status: 'failed' });
    } finally {
      if (goalsRequestClientIdRef.current === requestedId) {
        setLoadingGoals(false);
      }
    }
  };

  // Launch Speech Session
  const startSpeechSession = async (client: AssignedClient, isOverride = false, overrideReason = '') => {
    const now = new Date();
    const nowTs = now.getTime();
    const startTimeFormatted = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const sessionEncounterId = 'ENC-' + client.client_id + '-' + nowTs;
    if (plannedVisit?.serviceId) onClearPlannedVisit?.();
    setSelectedClient(client);
    setEncounterId(sessionEncounterId);
    setPersistedServiceId('');
    setPersistedDocId('');
    setSubmissionStarted(false);
    setPlannedServiceId('');
    setPlannedServiceCode('');
    setEncounterProviderId(client.provider_id || '');
    setClientGoals([]);
    setGoalsLoadState({ clientId: client.client_id, status: 'pending' });
    setIsAuthOverride(isOverride);
    setAuthOverrideReason(overrideReason || (isOverride ? 'Provider clinical override for active speech treatment session without active auth' : ''));
    setDos(now.toISOString().split('T')[0]);
    setStartTimeStr(startTimeFormatted);
    setEndTimeStr('');
    setStartTimestamp(nowTs);
    setLastActiveTimestamp(nowTs);
    setAccumulatedActiveSeconds(0);
    setElapsedSeconds(0);
    setIsMakeupVisit(false);
    setSelectedMissedServiceId('');
    setEligibleMissedVisits([]);
    setClientsTreatedTogether('');
    setServiceSetting('');
    setTelePatientLocation('');

    // Explicitly reset all session-specific clinical and signature state for a fresh encounter
    setGoalProgressMap({});
    setClinicianEvidence('');
    setSessionNarrative('');
    setSubjective('');
    setObjective('');
    setAssessment('');
    setPlan('');
    setShowSoapReview(false);
    setMedicalNecessity(
      'Skilled speech-language pathology intervention is medically necessary to remediate functional speech, language, and communication deficits impacting daily life activities and prevent developmental regression.'
    );
    setGuardianName('');
    setGuardianRelationship('Parent / Legal Guardian');
    setGuardianSignatureData('');
    setIsDrawing(false);
    setClinicianAttestationChecked(true);
    setValidationNotice(null);
    setHighlightedField(null);
    setSubmitSuccessResult(null);

    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }

    setCurrentView('session');

    // Fetch client details to prefill guardian if available
    try {
      const cRes = await fetch(`/api/clients/${client.client_id}`);
      if (cRes.ok) {
        const cData = await cRes.json();
        if (cData.client?.guardian_name) {
          setGuardianName(cData.client.guardian_name);
        }
        if (cData.client?.guardian_relationship) {
          setGuardianRelationship(cData.client.guardian_relationship);
        }
      }
    } catch (e) {
      console.warn('Could not fetch client details for guardian name prefill:', e);
    }

    await fetchClientGoals(client.client_id);
  };

  // Safe navigation back to Caseload without losing the active session
  const handleBackToCaseload = () => {
    // Navigating back to caseload is UI navigation, NOT an explicit clinician pause
    setCurrentView('caseload');
  };

  // Mobile/PWA popstate back navigation support: hardware back returns to caseload
  useEffect(() => {
    if (currentView === 'session') {
      window.history.pushState({ soapSessionOpen: true }, '');
      const handlePopState = () => {
        handleBackToCaseload();
      };
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [currentView]);

  // Return to the existing in-progress session with documented times/notes intact
  const handleResumeSession = () => {
    setCurrentView('session');
  };

  // Client card action: start or resume
  const handleStartSession = (client: AssignedClient, skipAuthWarning = false) => {
    if (selectedClient && selectedClient.client_id !== client.client_id) {
      setConflictClient(client);
      return;
    }
    if (selectedClient && selectedClient.client_id === client.client_id) {
      if (plannedVisit?.serviceId) onClearPlannedVisit?.();
      handleResumeSession();
      return;
    }

    // Warning follows the encounter preview. Only "none" is no covering authorization.
    if (!skipAuthWarning && caseloadShowsNoActiveAuth(client)) {
      setNoAuthWarningClient(client);
      return;
    }

    // Operational check: will starting this visit exceed expected weekly frequency?
    const weeklyCount = calculateWeeklyVisits(client.client_id);
    const freqStr = client.auth_frequency || client.expected_frequency || '2x/week';
    const freqMatch = freqStr.match(/(\d+)/);
    const targetWeekly = freqMatch ? parseInt(freqMatch[1], 10) : 2;

    if (weeklyCount >= targetWeekly) {
      setAboveFrequencyWarningClient(client);
      return;
    }

    const hasNoAuth = caseloadShowsNoActiveAuth(client);
    const isOverride = skipAuthWarning && hasNoAuth;
    startSpeechSession(client, isOverride, isOverride ? 'No active authorization override' : '');
  };

  // Discard unsigned session draft (removes draft, creates no service or clinical record)
  const handleDiscardSession = () => {
    if (draftHasRecoverableEncounter({ persistedServiceId, persistedDocId, submissionStarted })) {
      setShowDiscardModal(false);
      const notice = 'This encounter is already saved. Discard left the saved visit and its recovery reference in place.';
      setRecoveryNotice(notice);
      alert(notice);
      return;
    }
    if (selectedClient) {
      const provId = getCanonicalProviderId(user);
      fetch('/api/sessions/presence/clear', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          providerId: provId,
          clientId: selectedClient.client_id
        })
      }).catch(() => {});
    }

    clearActiveSessionFromStorage(user);

    setSelectedClient(null);
    setEncounterId('');
    setPersistedServiceId('');
    setPersistedDocId('');
    setPlannedServiceId('');
    setPlannedServiceCode('');
    setSubmissionStarted(false);
    setEncounterProviderId('');
    setIsAuthOverride(false);
    setAuthOverrideReason('');
    setClientGoals([]);
    setGoalsLoadState(null);
    setGoalProgressMap({});
    setDos(new Date().toISOString().split('T')[0]);
    setStartTimeStr('');
    setEndTimeStr('');
    setStartTimestamp(0);
    setLastActiveTimestamp(0);
    setAccumulatedActiveSeconds(0);
    setElapsedSeconds(0);
    setClinicianEvidence('');
    setSessionNarrative('');
    setSubjective('');
    setObjective('');
    setAssessment('');
    setPlan('');
    setShowSoapReview(false);
    setGuardianName('');
    setGuardianRelationship('Parent / Legal Guardian');
    setGuardianSignatureData('');
    setIsMakeupVisit(false);
    setSelectedMissedServiceId('');
    setEligibleMissedVisits([]);
    setClientsTreatedTogether('');
    setServiceSetting('');
    setTelePatientLocation('');
    setShowDiscardModal(false);
    setConflictClient(null);
    setCurrentView('caseload');
  };

  const parseTimeToHhMm = (value: string): string => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const iso = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (iso) {
      return `${String(Number(iso[1])).padStart(2, '0')}:${iso[2]}`;
    }
    const ampm = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(a\.?m\.?|p\.?m\.?)$/i);
    if (ampm) {
      let hours = Number(ampm[1]);
      const minutes = ampm[2];
      const mer = ampm[3].replace(/\./g, '').toUpperCase();
      if (mer === 'AM') {
        if (hours === 12) hours = 0;
      } else if (hours !== 12) {
        hours += 12;
      }
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }
    return '';
  };

  const minutesBetweenHhMm = (start: string, end: string): number => {
    const [hIn, mIn] = start.split(':').map(Number);
    const [hOut, mOut] = end.split(':').map(Number);
    if ([hIn, mIn, hOut, mOut].some((n) => Number.isNaN(n))) return 0;
    let diff = hOut * 60 + mOut - (hIn * 60 + mIn);
    if (diff < 0) diff += 24 * 60;
    return diff;
  };

  const timeInHhMm = parseTimeToHhMm(startTimeStr);
  const timeOutHhMm = parseTimeToHhMm(endTimeStr);
  const clockMinutes = timeInHhMm && timeOutHhMm
    ? Math.max(0, Math.min(240, Math.round(minutesBetweenHhMm(timeInHhMm, timeOutHhMm))))
    : null;
  const elapsedMinutes = clockMinutes !== null
    ? Math.max(1, clockMinutes)
    : Math.max(1, Math.round(elapsedSeconds / 60));
  const isUnder53Minutes = elapsedMinutes < 53;

  useEffect(() => {
    if (clockMinutes === null) return;
    const seconds = clockMinutes * 60;
    setElapsedSeconds((current) => (current === seconds ? current : seconds));
    setAccumulatedActiveSeconds((current) => (current === seconds ? current : seconds));
  }, [clockMinutes]);

  // Goal performance helpers
  const handleToggleGoal = (goalId: string) => {
    setGoalProgressMap((prev) => {
      const curr = prev[goalId] || { targeted: false, accuracy: 80, cueing: 'Mod Visual', evidenceNote: '' };
      return {
        ...prev,
        [goalId]: {
          ...curr,
          targeted: !curr.targeted
        }
      };
    });
  };

  const handleSetAccuracy = (goalId: string, acc: number) => {
    const clamped = Math.min(100, Math.max(0, acc));
    setGoalProgressMap((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        accuracy: clamped
      }
    }));
  };

  const handleSetCueing = (goalId: string, cueing: string) => {
    setGoalProgressMap((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        cueing
      }
    }));
  };

  const handleSetEvidence = (goalId: string, evidenceNote: string) => {
    setGoalProgressMap((prev) => ({
      ...prev,
      [goalId]: {
        ...prev[goalId],
        evidenceNote
      }
    }));
  };

  // Canvas Signature handlers
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = signatureCanvasPoint(canvas, e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const { x, y } = signatureCanvasPoint(canvas, e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0f172a';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) {
      setGuardianSignatureData(canvas.toDataURL());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setGuardianSignatureData('');
  };

  // Submit and Finalize SOAP
  useEffect(() => {
    const linkedId = String(plannedServiceId || persistedServiceId || '').trim();
    if (!linkedId) return;
    let cancelled = false;
    fetch(`/api/services/${linkedId}`)
      .then(async (res) => {
        if (!res.ok || cancelled) return;
        const row = await res.json();
        if (cancelled) return;
        if (String(row.status || '').toUpperCase() === 'SCHEDULED') {
          setRecoveryNotice(`This draft is linked to calendar appointment ${linkedId}. The appointment was not completed, and this draft was left unchanged.`);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [plannedServiceId, persistedServiceId]);

  const handleSubmitSession = async () => {
    if (!selectedClient) return;

    const linkedId = String(plannedServiceId || persistedServiceId || '').trim();
    if (linkedId) {
      try {
        const existingRes = await fetch(`/api/services/${linkedId}`);
        if (existingRes.ok) {
          const existingRow = await existingRes.json();
          if (String(existingRow.status || '').toUpperCase() === 'SCHEDULED') {
            setRecoveryNotice(`This draft is linked to calendar appointment ${linkedId}. The appointment was not completed, and this draft was left unchanged.`);
            return;
          }
        }
      } catch {
        // A failed lookup leaves the draft in place. Submission continues so the
        // server can refuse a calendar appointment, and ordinary recovery can retry.
      }
    }

    const goalsLoad = goalsLoadForClient(goalsLoadState, selectedClient.client_id);
    if (goalsLoad !== 'ready') {
      triggerValidationError(
        'goals',
        "This client's clinical goals have not finished loading. Wait for the goal list to load before attesting this note.",
        'mobile-soap-goals-section'
      );
      return;
    }

    // 1. Prepare targeted goals JSON from structured goal cards (primary Objective data)
    const targetedGoalsList = clientGoals
      .filter((g) => goalProgressMap[g.id]?.targeted)
      .map((g) => ({
        goal_id: g.id,
        description: g.description,
        accuracy: goalProgressMap[g.id].accuracy,
        cueing: goalProgressMap[g.id].cueing,
        evidence: goalProgressMap[g.id].evidenceNote
      }));

    setValidationNotice(null);

    // 0. Active client and date of service validation
    if (!selectedClient || !dos) {
      triggerValidationError('context', 'Active client appointment and date of service are required.', 'mobile-soap-context-section');
      return;
    }

    if (serviceSetting !== 'CLINIC' && serviceSetting !== 'HOME' && serviceSetting !== 'TELETHERAPY') {
      triggerValidationError(
        'setting',
        'Select clinic, home visit, or teletherapy before submitting. Community locations are not available.',
        'mobile-soap-setting-section'
      );
      return;
    }

    if (serviceSetting === 'TELETHERAPY' && telePatientLocation !== 'HOME' && telePatientLocation !== 'ELSEWHERE') {
      triggerValidationError(
        'setting',
        'Select whether the patient is at home or elsewhere for this teletherapy session.',
        'mobile-soap-setting-section'
      );
      return;
    }

    const resolvedPlaceOfService = placeOfServiceFromMobileSoapSetting(serviceSetting, telePatientLocation);
    if (!resolvedPlaceOfService) {
      triggerValidationError(
        'setting',
        'Service setting must be selected before submitting. Place of service cannot be assumed.',
        'mobile-soap-setting-section'
      );
      return;
    }

    if (
      clientsTreatedTogether !== 1 &&
      clientsTreatedTogether !== 2 &&
      clientsTreatedTogether !== 3
    ) {
      triggerValidationError(
        'clients_treated_together',
        'Select individual treatment (1 client) or group treatment (2 or 3 clients) before submitting.',
        'mobile-soap-group-section'
      );
      return;
    }

    // Require one missed visit to be selected before submitting when Makeup Visit is enabled
    if (isMakeupVisit && !selectedMissedServiceId) {
      triggerValidationError(
        'makeup',
        'Please select the eligible missed visit to link this makeup encounter before submitting.',
        'mobile-soap-makeup-section'
      );
      return;
    }

    // 0b. 53-Minute Minimum Duration Enforcement for completed treatment session
    if (elapsedMinutes < 53 && !isMakeupVisit) {
      triggerValidationError(
        'duration',
        `A completed billable treatment session requires at least 53 minutes of skilled intervention (current duration: ${elapsedMinutes} min). Please continue the session before attesting and submitting.`,
        'mobile-soap-timer-section'
      );
      return;
    }

    // 1. Authoritative objective evidence synthesized from structured targeted goals
    const structuredObjectiveSummary = targetedGoalsList.length > 0
      ? [
          'Targeted Goals Addressed:',
          ...targetedGoalsList.map((g, i) => {
            const accuracyText = g.accuracy === '' || g.accuracy === null || g.accuracy === undefined
              ? 'Not stored'
              : `${g.accuracy}${String(g.accuracy).includes('%') ? '' : '%'}`;
            return `${i + 1}. ${g.description} — Accuracy: ${accuracyText}, Cueing: ${g.cueing || 'Not stored'}${g.evidence ? ` [Trial evidence: ${g.evidence}]` : ''}`;
          })
        ].join('\n')
      : '';

    const finalObjective = objective.trim()
      ? (structuredObjectiveSummary && !objective.includes('Targeted Goals Addressed')
          ? `${structuredObjectiveSummary}\n\nAdditional Objective Notes:\n${objective.trim()}`
          : objective.trim())
      : structuredObjectiveSummary;

    // 2. Goal targeting validation (must have at least one targeted goal if client has goals)
    if (clientGoals.length > 0 && targetedGoalsList.length === 0) {
      triggerValidationError(
        'goals',
        'Please target at least one clinical goal with recorded accuracy before attesting note.',
        'mobile-soap-goals-section'
      );
      return;
    }

    // 3. Required Session Narrative / Subjective documentation validation
    if (!sessionNarrative.trim() && !subjective.trim()) {
      triggerValidationError(
        'narrative',
        'Session Narrative is required to document client presentation, response, and clinical intervention.',
        'mobile-soap-narrative-section'
      );
      return;
    }

    const finalSubjective = sessionNarrative.trim();
    const finalAssessment = '';
    const finalPlan = '';

    if (!finalSubjective) {
      triggerValidationError(
        'narrative',
        'Session Narrative is required to document client presentation, response, and clinical intervention.',
        'mobile-soap-narrative-section'
      );
      return;
    }

    // Teletherapy, and an approved clinic/home visit whose therapist chose SignWell, wait for the existing send.
    const requiresInPersonPad = mobileSoapRequiresInPersonPad({
      setting: serviceSetting,
      assignmentApproved: selectedClient.manual_soap_approved,
      signatureChoice: guardianSignatureChoice
    });
    const padLooksValid = guardianSignatureData.startsWith('data:image/') && guardianSignatureData.length > 200;
    if (requiresInPersonPad && (!guardianName.trim() || !guardianRelationship.trim() || !padLooksValid)) {
      triggerValidationError(
        'guardian',
        'Guardian/designee signature is required before this session note can be submitted.',
        'mobile-soap-guardian-section'
      );
      return;
    }

    // 5. Explicit clinician attestation / signature confirmation
    if (!clinicianAttestationChecked) {
      triggerValidationError(
        'attestation',
        'Clinician attestation must be confirmed before submitting note.',
        'mobile-soap-attestation-section'
      );
      return;
    }

    if (!isEncounterProviderResolved || !canonicalEncounterProvider) {
      triggerValidationError(
        'attestation',
        'Treating clinician identity could not be verified. Please contact the office before submitting this note.',
        'mobile-soap-attestation-section'
      );
      return;
    }

    if (isTreatingProviderMismatch) {
      triggerValidationError(
        'attestation',
        `Clinician signing restricted: Authenticated user (${user.name}) does not match the assigned treating clinician (${canonicalClinicianName}). Only the treating clinician can sign this note.`,
        'mobile-soap-attestation-section'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Reuse the planned visit, or one stable id stored before the request.
      const serviceIdForSubmit = stableSubmittedServiceId({
        plannedServiceId,
        persistedServiceId,
        encounterId: encounterId || `ENC-${selectedClient.client_id}-${startTimestamp}`
      });
      let createdServiceId = serviceIdForSubmit;
      setPersistedServiceId(serviceIdForSubmit);
      setSubmissionStarted(true);
      saveActiveSessionToStorage(user, {
        ...(loadActiveSessionFromStorage(user)?.data || {}),
        selectedClient,
        encounterId,
        persistedServiceId: serviceIdForSubmit,
        persistedDocId,
        plannedServiceId,
        plannedServiceCode,
        submissionStarted: true,
        dos,
        startTimeStr,
        endTimeStr,
        serviceSetting,
        telePatientLocation,
        clientsTreatedTogether
      });
      if (serviceIdForSubmit) {
        const servicePayload: Record<string, any> = {
          id: serviceIdForSubmit,
          idempotency_key: serviceIdForSubmit,
          client_id: selectedClient.client_id,
          provider_id: canonicalEncounterProvider.id,
          dos,
          service_code: plannedServiceCode || selectedClient.auth_service_code || '92507',
          start_time: timeInHhMm || startTimeStr || '',
          end_time: timeOutHhMm || '',
          duration_minutes: elapsedMinutes,
          status: 'COMPLETED',
          place_of_service: resolvedPlaceOfService,
          clients_treated_together: clientsTreatedTogether,
          completion_notes: 'Mobile Point-of-Care Session Completed'
        };

        if (isMakeupVisit && selectedMissedServiceId) {
          servicePayload.is_makeup = true;
          servicePayload.makeup_for_service_id = selectedMissedServiceId;
        }

        const svcRes = await fetch('/api/services', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(servicePayload)
        });

        const svcData = await svcRes.json().catch(() => ({}));
        const svcSync = readDisplaySync(svcData);
        if (svcSync.displayOutOfSync) {
          if (svcSync.serviceId) {
            createdServiceId = svcSync.serviceId;
            setPersistedServiceId(svcSync.serviceId);
            const storedSync = loadActiveSessionFromStorage(user);
            if (storedSync?.data) {
              saveActiveSessionToStorage(user, {
                ...storedSync.data,
                persistedServiceId: svcSync.serviceId,
                serviceSetting,
                telePatientLocation,
                clientsTreatedTogether
              });
            }
          }
          alert(svcSync.warning || SAVED_DISPLAY_WARNING);
          return;
        }
        if (!svcRes.ok) {
          throw new Error(svcData.error || 'Failed to create canonical service record');
        }

        createdServiceId = svcSync.serviceId || serviceIdForSubmit;
        setPersistedServiceId(createdServiceId);
        const stored = loadActiveSessionFromStorage(user);
        if (stored?.data) {
          saveActiveSessionToStorage(user, {
            ...stored.data,
            persistedServiceId: createdServiceId,
            submissionStarted: true,
            serviceSetting,
            telePatientLocation,
            clientsTreatedTogether
          });
        }
        const existingStatus = String(svcData.service?.status || '').toUpperCase();
        const alreadyCompleted = Boolean(svcData.alreadyExisted) && existingStatus === 'COMPLETED';
        if (svcData.alreadyExisted && !alreadyCompleted) {
          const visitUpdateRes = await fetch(`/api/services/${createdServiceId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              start_time: timeInHhMm || startTimeStr || '',
              end_time: timeOutHhMm || '',
              duration_minutes: elapsedMinutes,
              actual_duration_minutes: elapsedMinutes,
              place_of_service: resolvedPlaceOfService,
              clients_treated_together: clientsTreatedTogether
            })
          });
          const visitUpdateData = await visitUpdateRes.json().catch(() => ({}));
          if (!visitUpdateRes.ok) {
            throw new Error(visitUpdateData.error || 'Failed to save the planned visit');
          }
          const completeRes = await fetch(`/api/services/${createdServiceId}/complete`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              actual_duration_minutes: elapsedMinutes,
              notes: 'Mobile Point-of-Care Session Completed'
            })
          });
          const completeData = await completeRes.json().catch(() => ({}));
          if (!completeRes.ok) {
            throw new Error(completeData.error || 'Failed to complete the planned visit');
          }
        }
      } else {
        const svcUpdateRes = await fetch(`/api/services/${createdServiceId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            place_of_service: resolvedPlaceOfService,
            clients_treated_together: clientsTreatedTogether
          })
        });
        const svcUpdateData = await svcUpdateRes.json().catch(() => ({}));
        const updateSync = readDisplaySync(svcUpdateData);
        if (updateSync.displayOutOfSync) {
          alert(updateSync.warning || SAVED_DISPLAY_WARNING);
          return;
        }
        if (!svcUpdateRes.ok) {
          throw new Error(svcUpdateData.error || 'Failed to save encounter setting');
        }
        const retryComplete = await fetch(`/api/services/${createdServiceId}/complete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            actual_duration_minutes: elapsedMinutes,
            notes: 'Mobile Point-of-Care Session Completed'
          })
        });
        const retryCompleteData = await retryComplete.json().catch(() => ({}));
        if (!retryComplete.ok && retryComplete.status !== 400) {
          throw new Error(retryCompleteData.error || 'Failed to reuse the saved visit');
        }
      }

      // 3. Create canonical clinical document via dedicated trusted Mobile SOAP endpoint
      const isSlpa = isEncounterSlpa;
      const docRes = await fetch('/api/clinical/docs/mobile-soap', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_id: selectedClient.client_id,
          provider_id: canonicalEncounterProvider.id,
          doc_type: 'SOAP',
          service_id: createdServiceId,
          title: defaultSoapTitle({
            serviceCode: selectedClient.auth_service_code,
            dos,
            style: 'mobile'
          }),
          subjective: finalSubjective,
          objective: finalObjective,
          assessment: finalAssessment,
          plan: finalPlan,
          medical_necessity: medicalNecessity.trim() === 'Skilled speech-language pathology intervention is medically necessary to remediate functional speech, language, and communication deficits impacting daily life activities and prevent developmental regression.' ? '' : medicalNecessity.trim(),
          goals_addressed_json: JSON.stringify(targetedGoalsList),
          section_data_json: JSON.stringify({
            session_narrative: sessionNarrative.trim(),
            clinician_evidence: clinicianEvidence.trim(),
            additional_observations: objective.trim(),
            targeted_goals: targetedGoalsList,
            dos,
            time_in: timeInHhMm || '',
            time_out: timeOutHhMm || '',
            duration_minutes: elapsedMinutes,
            service_code: selectedClient.auth_service_code || '',
            clients_treated_together: clientsTreatedTogether,
            place_of_service: resolvedPlaceOfService,
            service_setting: serviceSetting
          }),
          clinician_signature: canonicalEncounterProvider.name
        })
      });

      if (!docRes.ok) {
        const err = await docRes.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create canonical clinical document');
      }

      const docData = await docRes.json();
      const docId = docData.docId || docData.document_id || persistedDocId;
      if (docId) {
        setPersistedDocId(docId);
        const storedAfterDoc = loadActiveSessionFromStorage(user);
        if (storedAfterDoc?.data) {
          saveActiveSessionToStorage(user, {
            ...storedAfterDoc.data,
            persistedServiceId: createdServiceId,
            persistedDocId: docId
          });
        }
      }

      if (docData.alreadyExisted) {
        const comparison = compareSoapGoalPerformance(targetedGoalsList, docData.goals_addressed_json);
        if (comparison !== 'match') {
          triggerValidationError(
            'goals',
            comparison === 'unreadable'
              ? 'This visit is already saved. Comparison of the stored goal results could not be completed.'
              : 'This visit is already saved. The goal results shown here differ and were not saved.',
            'mobile-soap-goals-section'
          );
          return;
        }
      }

      if (requiresInPersonPad) {
        const guardRes = await fetch(`/api/clinical/docs/${docId}/sign-guardian`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            guardianName: guardianName.trim(),
            relationship: guardianRelationship.trim(),
            signatureEvidence: guardianSignatureData
          })
        });
        if (!guardRes.ok) {
          const err = await guardRes.json().catch(() => ({}));
          throw new Error(formatPartialSoapSaveError(
            err.error || 'Guardian/designee signature is required before this session note can be submitted.',
            docId,
            createdServiceId
          ));
        }
      }

      // 5. Sign clinician (which handles SLPA auto-routing to PENDING_SUPERVISION!)
      const signRes = await fetch(`/api/clinical/docs/${docId}/sign-clinician`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signerName: canonicalEncounterProvider.name
        })
      });
      if (!signRes.ok) {
        const err = await signRes.json().catch(() => ({}));
        throw new Error(formatPartialSoapSaveError(
          err.error || 'Clinician signature could not be saved.',
          docId,
          createdServiceId
        ));
      }

      const signData = await signRes.json().catch(() => ({}));
      const interpreted = interpretSoapSigningResponses({
        guardianOk: true,
        clinicianOk: true,
        clinicianStatus: signData.status,
        isSlpa
      });
      if (!interpreted.canShowSuccess || interpreted.kind === 'failed') {
        throw new Error(formatPartialSoapSaveError(
          'Session note was saved but could not be confirmed as complete.',
          docId,
          createdServiceId
        ));
      }
      const finalStatus = interpreted.status;

      // Clean up localStorage active session immediately
      clearActiveSessionFromStorage(user);
      if (selectedClient) {
        const provId = getCanonicalProviderId(user);
        fetch('/api/sessions/presence/clear', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            providerId: provId,
            clientId: selectedClient.client_id
          })
        }).catch(() => {});
      }
      setIsAuthOverride(false);
      setAuthOverrideReason('');

      // Reset active in-memory draft state so returning to Mobile SOAP cannot reopen
      if (onClearPreselectedClient) {
        onClearPreselectedClient();
      }
      setSelectedClient(null);
      setEncounterId('');
      setPersistedServiceId('');
      setPersistedDocId('');
      setPlannedServiceId('');
      setPlannedServiceCode('');
      setSubmissionStarted(false);
      setEncounterProviderId('');
      setClientGoals([]);
      setGoalsLoadState(null);
      setGoalProgressMap({});
      setClinicianEvidence('');
      setSessionNarrative('');
      setSubjective('');
      setObjective('');
      setAssessment('');
      setPlan('');
      setShowSoapReview(false);
      setGuardianName('');
      setGuardianRelationship('Parent / Legal Guardian');
      setGuardianSignatureData('');
      setIsMakeupVisit(false);
      setSelectedMissedServiceId('');
      setEligibleMissedVisits([]);
      setClientsTreatedTogether('');
      setServiceSetting('');
      setTelePatientLocation('');
      setElapsedSeconds(0);
      setAccumulatedActiveSeconds(0);
      setLastActiveTimestamp(0);
      setStartTimestamp(0);

      let treatingClinician = '';
      if (createdServiceId) {
        try {
          const savedSvcRes = await fetch(`/api/services/${createdServiceId}`);
          if (savedSvcRes.ok) {
            treatingClinician = resolveSavedServiceTreatingClinician(await savedSvcRes.json());
          }
        } catch {
          treatingClinician = '';
        }
      }

      setSubmitSuccessResult({
        docId,
        status: finalStatus,
        serviceId: createdServiceId,
        treatingClinician,
        isSlpa,
        awaitingRemoteGuardian: !requiresInPersonPad,
        kind: interpreted.kind === 'pending_supervision'
          ? 'pending_supervision'
          : interpreted.kind === 'finalized'
            ? 'finalized'
            : 'pending_signatures'
      });
      playPulseSeal();
      setCurrentView('success');
    } catch (err: any) {
      console.error('Submission failed:', err);
      if (err?.displayOutOfSync || err?.message === SAVED_DISPLAY_WARNING) {
        alert(err.message || SAVED_DISPLAY_WARNING);
      } else {
        alert('Error finalizing session: ' + (err.message || 'Unknown error'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper for weekly stats
  const calculateWeeklyVisits = (clientId: string) => {
    const now = new Date();
    const day = now.getDay();
    const sunday = new Date(now);
    sunday.setDate(now.getDate() - day);
    sunday.setHours(0, 0, 0, 0);
    const saturday = new Date(sunday);
    saturday.setDate(sunday.getDate() + 6);
    saturday.setHours(23, 59, 59, 999);

    const clientServicesInWeek = services.filter((s) => {
      if (s.client_id !== clientId) return false;
      if (s.status === 'CANCELLED' || s.status === 'NO_SHOW') return false;
      const sDate = new Date(s.dos + 'T00:00:00');
      return sDate >= sunday && sDate <= saturday;
    });

    return clientServicesInWeek.length;
  };

  // Filtered Caseload - authoritative deterministic search (strictly ACTIVE clients)
  const filteredAssignments = assignments.filter((a) => {
    if (a.client_status && a.client_status.toUpperCase() !== 'ACTIVE') return false;
    const q = searchQuery.toLowerCase().trim();
    if (!q) return true;
    const fullName = `${a.client_first_name} ${a.client_last_name}`.toLowerCase();
    const reverseName = `${a.client_last_name}, ${a.client_first_name}`.toLowerCase();
    const ahcccs = (a.ahcccs_id || '').toLowerCase();
    const clientId = (a.client_id || '').toLowerCase();
    return (
      fullName.includes(q) ||
      reverseName.includes(q) ||
      ahcccs.includes(q) ||
      clientId.includes(q)
    );
  });

  // ==========================================
  // RENDER: SUCCESS SCREEN
  // ==========================================
  if (currentView === 'success' && submitSuccessResult) {
    const confirmationKind = submitSuccessResult.kind;
    const statusTone = confirmationKind === 'finalized'
      ? 'text-emerald-800'
      : 'text-amber-800';

    return (
      <div className="max-w-xl mx-auto py-8 px-4 text-center space-y-6">
        <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center border bg-emerald-50 text-emerald-600 border-emerald-200">
          <CheckCircle2 className="w-10 h-10" />
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            Session Successfully Documented
          </h2>
          <p className="text-sm text-slate-600">
            <span className={`${statusTone} font-semibold`}>
              {soapConfirmationMessage(confirmationKind)}{' '}
              {submitSuccessResult.awaitingRemoteGuardian
                ? 'Guardian signature is still outstanding and is requested with Send to Guardian for eSign. '
                : ''}
              Status:{' '}
              <span className="font-mono px-1.5 py-0.5 rounded border bg-white text-slate-800 border-slate-200">
                {submitSuccessResult.status}
              </span>
            </span>
          </p>
        </div>

        <div className="rounded-xl p-4 text-left font-mono text-xs space-y-1.5 border bg-white border-slate-200/90 text-slate-800 shadow-[0_1px_3px_rgba(15,23,42,0.05)]">
          <div className="flex justify-between">
            <span className="text-slate-500">Document ID:</span>
            <span className="font-semibold text-slate-900">{submitSuccessResult.docId}</span>
          </div>
          {submitSuccessResult.serviceId && (
            <div className="flex justify-between">
              <span className="text-slate-500">Service Record:</span>
              <span className="font-semibold text-slate-900">{submitSuccessResult.serviceId}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-slate-500">Treating Clinician:</span>
            <span className="text-slate-800">{submitSuccessResult.treatingClinician}</span>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          <button
            onClick={() => {
              setCurrentView('caseload');
              setSelectedClient(null);
              setSubmitSuccessResult(null);
              fetchCaseload();
            }}
            className="flex-1 py-3.5 text-white font-bold rounded-xl text-sm transition-all shadow-md cursor-pointer bg-slate-900 hover:bg-slate-800 shadow-xs"
          >
            Return to My Caseload
          </button>
          {onNavigateTab && (
            <button
              onClick={() => onNavigateTab('clinical', submitSuccessResult.docId)}
              className="py-3.5 px-5 font-semibold rounded-xl text-sm border transition-all cursor-pointer bg-white hover:bg-slate-50 text-slate-700 border-slate-200/90 shadow-xs"
            >
              View in Clinical Docs
            </button>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // RENDER: ACTIVE SESSION
  // ==========================================
  if (currentView === 'session' && selectedClient) {
    const isAssistant = user.role === 'ASSISTANT';
    const isSupervisor = user.role === 'SUPERVISOR';
    const isLightMobile = isAssistant || isSupervisor;
    const targetedCount = Object.values(goalProgressMap).filter((g: TargetedGoalProgress) => g.targeted).length;
    const selectedMissedService = eligibleMissedVisits.find((v) => v.id === selectedMissedServiceId);

    return (
      <div className="max-w-2xl mx-auto space-y-6 pb-28">
        {/* Sticky Mobile HUD Header */}
        <div id="mobile-soap-context-section" className={`sticky top-0 z-20 -mx-4 px-3.5 py-2.5 backdrop-blur-md border-b flex flex-col gap-2 transition-all ${
          isLightMobile
            ? `bg-white/95 border-slate-200/90 shadow-xs ${highlightedField === 'context' ? 'border-amber-400 ring-2 ring-amber-400/40' : ''}`
            : `bg-[#0c0e12]/95 shadow-xl ${highlightedField === 'context' ? 'border-amber-400 ring-2 ring-amber-400/40' : 'border-slate-800/90'}`
        }`}>
          {/* Row 1 — CONTROLS ONLY */}
          <div className="flex items-center justify-between gap-2 min-w-0">
            <button
              type="button"
              onClick={handleBackToCaseload}
              className={`flex items-center space-x-1.5 text-xs px-2.5 py-1.5 rounded-lg border font-semibold min-h-[40px] transition-all duration-150 cursor-pointer shrink-0 ${
                isLightMobile
                  ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200/90 shadow-xs'
                  : 'bg-slate-900/90 hover:bg-slate-800/90 text-slate-300 hover:text-white border-slate-700/80'
              }`}
              title="Return to My Caseload (session draft safely preserved)"
            >
              <ArrowLeft className={`w-4 h-4 ${isLightMobile ? 'text-slate-600' : 'text-slate-300'}`} />
              <span>Back to Caseload</span>
            </button>

            <div className="flex items-center space-x-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setShowDiscardModal(true)}
                className={`p-2 rounded-lg border min-h-[40px] min-w-[40px] flex items-center justify-center transition-all duration-150 cursor-pointer ${
                  isLightMobile
                    ? 'bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-700 border-slate-200/90'
                    : 'bg-slate-900/90 hover:bg-rose-950/40 border-slate-700/80 text-slate-400 hover:text-rose-300'
                }`}
                title="Discard / Cancel this unsigned session"
              >
                <Trash2 className="w-4 h-4 shrink-0" />
              </button>
            </div>
          </div>

          {/* Row 2 — CLIENT IDENTITY */}
          <div className="w-full text-center py-0.5">
            <div className={`text-sm sm:text-base font-bold leading-snug ${
              isLightMobile ? 'text-slate-900' : 'text-slate-100'
            }`}>
              {selectedClient.client_last_name}, {selectedClient.client_first_name}
            </div>
          </div>

          {/* Row 3 — Encounter timing summary */}
          <div className={`flex items-center justify-between gap-2 text-[11px] font-mono pt-1.5 border-t px-0.5 ${
            isLightMobile ? 'border-slate-100 text-slate-500' : 'border-slate-800/60 text-slate-400'
          }`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="font-sans">DOS</span>
              <span className={`font-semibold truncate ${isLightMobile ? 'text-slate-800' : 'text-slate-200'}`}>{dos}</span>
            </div>
            <div className={`flex items-center gap-1.5 shrink-0 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>
              <span>{timeInHhMm || '—'}–{timeOutHhMm || '—'}</span>
              <span className={isLightMobile ? 'text-slate-300' : 'text-slate-600'}>•</span>
              <span>{elapsedMinutes} min</span>
            </div>
          </div>
        </div>

        {/* Operational Validation Alert Banner (Top) */}
        {validationNotice && (
          <div className={`rounded-2xl p-4 flex items-start justify-between gap-3 animate-in fade-in slide-in-from-top-2 duration-200 border ${
            isLightMobile
              ? 'bg-amber-50 border-amber-200 text-amber-900 shadow-xs'
              : 'bg-amber-950/60 border-amber-500/60 text-amber-200 shadow-lg'
          }`}>
            <div className="flex items-start space-x-2.5">
              <AlertTriangle className={`w-5 h-5 shrink-0 mt-0.5 ${isLightMobile ? 'text-amber-600' : 'text-amber-400'}`} />
              <div className="space-y-0.5">
                <div className={`text-xs font-bold uppercase tracking-wider ${isLightMobile ? 'text-amber-900' : 'text-amber-300'}`}>
                  Required Field Incomplete
                </div>
                <div className={`text-xs font-medium leading-relaxed ${isLightMobile ? 'text-amber-800' : 'text-amber-100'}`}>
                  {validationNotice.message}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 shrink-0">
              <button
                type="button"
                onClick={() => {
                  const targetId =
                    validationNotice.field === 'narrative' ? 'mobile-soap-narrative-section' :
                    validationNotice.field === 'goals' ? 'mobile-soap-goals-section' :
                    validationNotice.field === 'objective' ? 'mobile-soap-objective-section' :
                    validationNotice.field === 'guardian' ? 'mobile-soap-guardian-section' :
                    validationNotice.field === 'attestation' ? 'mobile-soap-attestation-section' :
                    validationNotice.field === 'makeup' ? 'mobile-soap-makeup-section' :
                    validationNotice.field === 'setting' ? 'mobile-soap-setting-section' :
                    validationNotice.field === 'clients_treated_together' ? 'mobile-soap-group-section' :
                    'mobile-soap-context-section';
                  const el = document.getElementById(targetId);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const interactiveEl = el.querySelector('input, textarea, select') as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
                    if (interactiveEl) interactiveEl.focus();
                  }
                }}
                className={`px-2.5 py-1 text-[11px] font-bold border rounded-lg transition-colors cursor-pointer ${
                  isLightMobile
                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-300'
                    : 'bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-300'
                }`}
              >
                Jump to Field
              </button>
              <button
                type="button"
                onClick={() => setValidationNotice(null)}
                className={`p-1 rounded-lg transition-colors ${
                  isLightMobile ? 'text-amber-700 hover:text-amber-950' : 'text-amber-400 hover:text-white'
                }`}
                title="Dismiss warning"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Encounter timing — documented fields, no live stopwatch */}
        <div
          id="mobile-soap-timer-section"
          className={`rounded-2xl p-4 space-y-3 border ${
            highlightedField === 'duration'
              ? 'border-amber-400 ring-2 ring-amber-400/40 bg-amber-50'
              : isLightMobile
              ? 'bg-white border-slate-200/90 shadow-xs'
              : 'bg-gradient-to-b from-[#162438] via-[#121c2e] to-[#0e1625] border-slate-700/70 shadow-md'
          }`}
        >
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
            <div>
              <label className={`block text-[10px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>Date of Service</label>
              <input
                type="date"
                value={dos}
                onChange={(e) => setDos(e.target.value)}
                className={`w-full rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 ${
                  isLightMobile
                    ? 'bg-slate-50/70 border border-slate-200 text-slate-800 focus:border-slate-400 focus:ring-slate-400/40'
                    : 'bg-[#0a101b] border border-slate-700 text-slate-100 focus:border-slate-500 focus:ring-slate-500/30'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-[10px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>Time In</label>
              <input
                type="time"
                value={timeInHhMm}
                onChange={(e) => {
                  setStartTimeStr(e.target.value);
                  setLastActiveTimestamp(Date.now());
                }}
                className={`w-full rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 ${
                  isLightMobile
                    ? 'bg-slate-50/70 border border-slate-200 text-slate-800 focus:border-slate-400 focus:ring-slate-400/40'
                    : 'bg-[#0a101b] border border-slate-700 text-slate-100 focus:border-slate-500 focus:ring-slate-500/30'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-[10px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>Time Out</label>
              <input
                type="time"
                value={timeOutHhMm}
                onChange={(e) => {
                  setEndTimeStr(e.target.value);
                  setLastActiveTimestamp(Date.now());
                }}
                className={`w-full rounded-lg px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-1 ${
                  isLightMobile
                    ? 'bg-slate-50/70 border border-slate-200 text-slate-800 focus:border-slate-400 focus:ring-slate-400/40'
                    : 'bg-[#0a101b] border border-slate-700 text-slate-100 focus:border-slate-500 focus:ring-slate-500/30'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-[10px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>Duration</label>
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  readOnly
                  tabIndex={-1}
                  value={elapsedMinutes}
                  aria-readonly="true"
                  className={`w-full rounded-lg px-2.5 py-1.5 text-sm font-mono font-semibold focus:outline-none ${
                    isLightMobile
                      ? 'bg-slate-100 border border-slate-200 text-slate-800'
                      : 'bg-[#0a101b] border border-slate-700 text-slate-100'
                  }`}
                />
                <span className={`text-sm font-semibold font-mono shrink-0 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>min</span>
              </div>
            </div>
            <div id="mobile-soap-setting-section" className="col-span-2 sm:col-span-4 space-y-3">
              <div>
                <label className={`block text-[10px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>
                  Service setting
                </label>
                <select
                  value={serviceSetting}
                  onChange={(e) => {
                    const next = e.target.value;
                    const setting = next === 'CLINIC' || next === 'HOME' || next === 'TELETHERAPY' ? next : '';
                    setServiceSetting(setting);
                    if (setting !== 'TELETHERAPY') setTelePatientLocation('');
                  }}
                  className={`w-full rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 ${
                    highlightedField === 'setting'
                      ? 'border-amber-400 ring-2 ring-amber-400/40'
                      : isLightMobile
                      ? 'bg-slate-50/70 border border-slate-200 text-slate-800 focus:border-slate-400 focus:ring-slate-400/40'
                      : 'bg-[#0a101b] border border-slate-700 text-slate-100 focus:border-slate-500 focus:ring-slate-500/30'
                  }`}
                  required
                >
                  <option value="">Select clinic, home visit, or teletherapy…</option>
                  <option value="CLINIC">Clinic</option>
                  <option value="HOME">Home visit</option>
                  <option value="TELETHERAPY">Teletherapy</option>
                </select>
              </div>
              {serviceSetting === 'TELETHERAPY' && (
                <div>
                  <label className={`block text-[10px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>
                    Patient location during teletherapy
                  </label>
                  <select
                    value={telePatientLocation}
                    onChange={(e) => {
                      const next = e.target.value;
                      setTelePatientLocation(next === 'HOME' || next === 'ELSEWHERE' ? next : '');
                    }}
                    className={`w-full rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 ${
                      highlightedField === 'setting'
                        ? 'border-amber-400 ring-2 ring-amber-400/40'
                        : isLightMobile
                        ? 'bg-slate-50/70 border border-slate-200 text-slate-800 focus:border-slate-400 focus:ring-slate-400/40'
                        : 'bg-[#0a101b] border border-slate-700 text-slate-100 focus:border-slate-500 focus:ring-slate-500/30'
                    }`}
                    required
                  >
                    <option value="">Select patient at home or elsewhere…</option>
                    <option value="HOME">Patient at home</option>
                    <option value="ELSEWHERE">Patient elsewhere</option>
                  </select>
                </div>
              )}
            </div>
            <div id="mobile-soap-group-section" className="col-span-2 sm:col-span-4">
              <label className={`block text-[10px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>
                Clients treated together
              </label>
              <select
                value={clientsTreatedTogether === 1 || clientsTreatedTogether === 2 || clientsTreatedTogether === 3 ? String(clientsTreatedTogether) : ''}
                onChange={(e) => {
                  const next = e.target.value;
                  setClientsTreatedTogether(next === '1' || next === '2' || next === '3' ? Number(next) as 1 | 2 | 3 : '');
                }}
                className={`w-full rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 ${
                  highlightedField === 'clients_treated_together'
                    ? 'border-amber-400 ring-2 ring-amber-400/40'
                    : isLightMobile
                    ? 'bg-slate-50/70 border border-slate-200 text-slate-800 focus:border-slate-400 focus:ring-slate-400/40'
                    : 'bg-[#0a101b] border border-slate-700 text-slate-100 focus:border-slate-500 focus:ring-slate-500/30'
                }`}
                required
              >
                <option value="">Select individual or group…</option>
                <option value="1">Individual treatment (1 client)</option>
                <option value="2">Group treatment (2 clients)</option>
                <option value="3">Group treatment (3 clients)</option>
              </select>
            </div>
          </div>

          {isUnder53Minutes ? (
            <div className={`flex items-center justify-center space-x-2 text-xs px-3.5 py-1.5 rounded-xl text-center border ${
              isLightMobile
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-[#0a101b] border-amber-500/40 text-amber-300'
            }`}>
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${isLightMobile ? 'text-amber-600' : 'text-amber-400'}`} />
              <span className="font-semibold">Minimum session duration not met ({elapsedMinutes} of 53 min).</span>
            </div>
          ) : (
            <div className={`flex items-center justify-center space-x-2 text-xs px-3.5 py-1.5 rounded-xl text-center border ${
              isLightMobile
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : 'bg-[#0a101b] border-emerald-500/40 text-emerald-300'
            }`}>
              <Check className={`w-4 h-4 flex-shrink-0 ${isLightMobile ? 'text-emerald-600' : 'text-emerald-400'}`} />
              <span className="font-semibold">Minimum session duration met (53+ minutes).</span>
            </div>
          )}

          {recoveryNotice && (
            <div className={`text-xs p-2 rounded-xl text-center border ${
              isLightMobile
                ? 'bg-sky-50 border-sky-200 text-sky-900'
                : 'bg-[#0a101b] border-sky-500/40 text-sky-200'
            }`}>
              {recoveryNotice}
            </div>
          )}
        </div>

        {/* Makeup Visit Encounter Control (Only displayed when 1+ eligible missed visits exist) */}
        {eligibleMissedVisits.length > 0 && (
          <div
            id="mobile-soap-makeup-section"
            className={`rounded-2xl p-4 transition-all space-y-3 border ${
              highlightedField === 'makeup'
                ? 'border-amber-400 ring-2 ring-amber-400/40 bg-amber-50'
                : isLightMobile
                ? 'bg-white border-slate-200/90 shadow-xs'
                : 'bg-gradient-to-b from-[#162438] via-[#121c2e] to-[#0e1625] border-slate-700/70 shadow-md'
            }`}
          >
            <div className="flex items-center justify-between">
              <label
                htmlFor="mobile-soap-makeup-toggle"
                className="flex items-center space-x-2.5 cursor-pointer select-none"
              >
                <input
                  id="mobile-soap-makeup-toggle"
                  type="checkbox"
                  checked={isMakeupVisit}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIsMakeupVisit(checked);
                    if (checked && !selectedMissedServiceId && eligibleMissedVisits.length > 0) {
                      setSelectedMissedServiceId(eligibleMissedVisits[0].id);
                    }
                  }}
                  className={`w-4 h-4 rounded cursor-pointer ${
                    isLightMobile
                      ? 'border-slate-300 text-cyan-600 focus:ring-cyan-500'
                      : 'border-slate-700 bg-slate-950 text-indigo-600 focus:ring-indigo-500'
                  }`}
                />
                <span className={`text-xs font-semibold ${isLightMobile ? 'text-slate-800' : 'text-slate-200'}`}>
                  Makeup Visit
                </span>
                <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${
                  isLightMobile
                    ? 'bg-slate-100 text-slate-600 border-slate-200'
                    : 'bg-slate-800/80 text-slate-400 border-slate-700/60'
                }`}>
                  {eligibleMissedVisits.length} eligible
                </span>
              </label>

              {isMakeupVisit && selectedMissedService && (
                <span className={`text-[11px] font-medium truncate max-w-[210px] text-right ${
                  isLightMobile ? 'text-cyan-800 font-semibold' : 'text-indigo-300'
                }`}>
                  Makeup for missed visit — {formatMissedDos(selectedMissedService.dos)}
                </span>
              )}
            </div>

            {isMakeupVisit && (
              <div className={`space-y-2 pt-2 border-t ${isLightMobile ? 'border-slate-100' : 'border-slate-800/80'}`}>
                <label
                  htmlFor="mobile-soap-missed-selector"
                  className={`block text-[10px] font-bold uppercase tracking-wider ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}
                >
                  Select Original Missed Visit
                </label>
                <select
                  id="mobile-soap-missed-selector"
                  value={selectedMissedServiceId}
                  onChange={(e) => setSelectedMissedServiceId(e.target.value)}
                  className={`w-full rounded-xl px-3 py-2 text-xs font-medium focus:outline-none ${
                    isLightMobile
                      ? 'bg-slate-50 border border-slate-200 text-slate-900 focus:border-cyan-500'
                      : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 focus:border-indigo-500'
                  }`}
                >
                  <option value="" disabled>-- Select an eligible missed visit --</option>
                  {eligibleMissedVisits.map((item) => (
                    <option key={item.id} value={item.id}>
                      {formatMissedDos(item.dos)} • {item.service_code || 'CPT'} ({item.status}{item.cancellation_reason ? ` - ${item.cancellation_reason}` : ''})
                    </option>
                  ))}
                </select>

                {selectedMissedService && (
                  <div className={`text-xs px-3 py-2 rounded-xl flex items-center justify-between border ${
                    isLightMobile
                      ? 'bg-cyan-50 text-cyan-900 border-cyan-200'
                      : 'bg-indigo-950/40 text-indigo-300 border-indigo-500/30'
                  }`}>
                    <span className="font-semibold">
                      Makeup for missed visit — {formatMissedDos(selectedMissedService.dos)}
                    </span>
                    <span className={`text-[10px] font-mono ${isLightMobile ? 'text-cyan-700' : 'text-indigo-400'}`}>
                      {selectedMissedService.service_code} • {selectedMissedService.status}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Unified SOAP Documentation Shell */}
        <div className={`rounded-2xl p-4 sm:p-5 space-y-5 border ${
          isLightMobile
            ? 'bg-white border-slate-200/90 shadow-xs'
            : 'bg-gradient-to-b from-[#162438] via-[#121c2e] to-[#0e1625] border-slate-700/70 shadow-md'
        }`}>
          {/* 1. Quick Clinician Observation / Evidence */}
          <div id="mobile-soap-poc-section" className="space-y-2.5">
            <div className="flex items-center justify-center">
              <label htmlFor="mobile-soap-poc-notes" className={`block text-xs font-bold uppercase tracking-wider text-center ${
                isLightMobile ? 'text-slate-800' : 'text-slate-300'
              }`}>
                Point-of-Care Notes / Evidence
              </label>
            </div>
            <textarea
              id="mobile-soap-poc-notes"
              rows={2}
              value={clinicianEvidence}
              onChange={(e) => setClinicianEvidence(e.target.value)}
              className={`w-full rounded-xl p-3 text-xs sm:text-sm font-sans focus:outline-none ${
                isLightMobile
                  ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20'
                  : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30'
              }`}
            />
          </div>

          <div className={`border-t ${isLightMobile ? 'border-slate-100' : 'border-slate-800/80'}`} />

          {/* 2. Primary Clinician Writing Field: Session Narrative (Required) */}
          <div
            id="mobile-soap-narrative-section"
            className={`space-y-2.5 transition-all ${
              highlightedField === 'narrative' ? 'p-1.5 ring-1 ring-amber-400/50 rounded-xl bg-amber-50' : ''
            }`}
          >
            <div className="flex items-center justify-center space-x-2">
              <label htmlFor="mobile-soap-narrative" className={`block text-xs font-bold uppercase tracking-wider text-center ${
                isLightMobile ? 'text-slate-900' : 'text-slate-200'
              }`}>
                Session Narrative
              </label>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wider border ${
                isLightMobile
                  ? 'bg-slate-50 text-slate-700 border-slate-300'
                  : 'bg-amber-500/10 text-amber-300 border-amber-500/30'
              }`}>
                Required
              </span>
            </div>

            <textarea
              id="mobile-soap-narrative"
              rows={5}
              value={sessionNarrative}
              onChange={(e) => setSessionNarrative(e.target.value)}
              className={`w-full rounded-xl p-3.5 text-xs sm:text-sm leading-relaxed font-sans min-h-[130px] focus:outline-none ${
                highlightedField === 'narrative' ? 'border-amber-400 ring-2 ring-amber-400/40' : ''
              } ${
                isLightMobile
                  ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20'
                  : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30'
              }`}
            />
          </div>

          <div className={`border-t ${isLightMobile ? 'border-slate-100' : 'border-slate-800/80'}`} />

          {/* 3. Objective Data — Goals & Performance Section */}
          <div
            id="mobile-soap-goals-section"
            className={`space-y-3 transition-all ${
              highlightedField === 'goals' ? 'p-1.5 ring-1 ring-amber-400/50 rounded-xl bg-amber-50' : ''
            }`}
          >
            <div className="flex flex-col items-center justify-center gap-0.5">
              <h3 className={`text-xs font-bold uppercase tracking-wider flex items-center space-x-1.5 text-center ${
                isLightMobile ? 'text-slate-900' : 'text-slate-200'
              }`}>
                <Target className={`w-3.5 h-3.5 ${isLightMobile ? 'text-cyan-700' : 'text-sky-400'}`} />
                <span>Goals & Performance ({clientGoals.length})</span>
              </h3>
              <span className={`text-[11px] font-mono text-center ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                {targetedCount} targeted today
              </span>
            </div>

            {goalsLoadForClient(goalsLoadState, selectedClient?.client_id) === 'pending' || loadingGoals ? (
              <div className={`p-8 text-center text-xs flex items-center justify-center space-x-2 ${
                isLightMobile ? 'text-slate-500' : 'text-slate-400'
              }`}>
                <Loader2 className={`w-4 h-4 animate-spin ${isLightMobile ? 'text-cyan-600' : 'text-sky-400'}`} />
                <span>Loading canonical goals...</span>
              </div>
            ) : goalsLoadForClient(goalsLoadState, selectedClient?.client_id) === 'failed' ? (
              <div className={`rounded-xl p-4 text-center text-xs border ${
                isLightMobile
                  ? 'bg-slate-50 border-slate-200 text-slate-600'
                  : 'bg-slate-900/60 border-slate-800 text-slate-400'
              }`}>
                Clinical goals could not be loaded for this client. The session draft is unchanged.
              </div>
            ) : clientGoals.length === 0 ? (
              <div className={`rounded-xl p-4 text-center text-xs border ${
                isLightMobile
                  ? 'bg-slate-50 border-slate-200 text-slate-600'
                  : 'bg-slate-900/60 border-slate-800 text-slate-400'
              }`}>
                No active goals established yet in client chart. You can still document narrative notes below.
              </div>
            ) : (
              <div className="space-y-3">
                {clientGoals.map((goal, idx) => {
                  const prog = goalProgressMap[goal.id] || {
                    targeted: false,
                    accuracy: goal.target_percentage || 80,
                    cueing: 'Mod Visual',
                    evidenceNote: ''
                  };

                  return (
                    <div
                      key={goal.id}
                      className={`border rounded-2xl p-4 transition-all ${
                        isLightMobile
                          ? prog.targeted
                            ? 'bg-cyan-50/50 border-cyan-200/90 shadow-xs'
                            : 'bg-slate-50/70 border-slate-200'
                          : prog.targeted
                          ? 'bg-[#121a29] border-sky-500/60 shadow-md shadow-sky-500/5'
                          : 'bg-[#121a29]/90 border-slate-700/80'
                      }`}
                    >
                      {/* Goal Header & Toggle */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded border ${
                              isLightMobile
                                ? 'bg-slate-50 text-slate-700 border-slate-300'
                                : 'bg-slate-950 text-sky-300 border-slate-800'
                            }`}>
                              Goal #{idx + 1}
                            </span>
                            {goal.target_percentage && (
                              <span className={`text-[10px] font-mono ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                                Target: {goal.target_percentage}%
                              </span>
                            )}
                          </div>
                          <p className={`text-xs leading-relaxed font-medium ${
                            isLightMobile ? 'text-slate-900' : 'text-slate-200'
                          }`}>
                            {goal.description}
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => handleToggleGoal(goal.id)}
                          className={`min-h-[44px] px-3.5 py-2 rounded-xl text-xs font-bold flex items-center space-x-1.5 transition-all duration-150 cursor-pointer ${
                            prog.targeted
                              ? 'bg-cyan-600 hover:bg-cyan-700 text-white shadow-xs'
                              : isLightMobile
                              ? 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 shadow-xs'
                              : 'bg-gradient-to-b from-[#162033] via-[#0f172a] to-[#0a0f1d] text-slate-200 hover:text-white border border-slate-700/80'
                          }`}
                        >
                          {prog.targeted ? (
                            <>
                              <Check className="w-4 h-4" />
                              <span>Targeted</span>
                            </>
                          ) : (
                            <span>Target Today</span>
                          )}
                        </button>
                      </div>

                      {/* Targeted Controls */}
                      {prog.targeted && (
                        <div className={`mt-4 pt-3 border-t space-y-3 ${isLightMobile ? 'border-slate-200' : 'border-slate-800'}`}>
                          {/* Accuracy Stepper & Presets */}
                          <div>
                            <div className={`flex items-center justify-between text-xs font-semibold mb-1.5 ${
                              isLightMobile ? 'text-slate-700' : 'text-slate-300'
                            }`}>
                              <span>Accuracy / Performance:</span>
                              <span className={`font-mono text-sm font-bold ${isLightMobile ? 'text-cyan-700' : 'text-sky-400'}`}>
                                {prog.accuracy}%
                              </span>
                            </div>

                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleSetAccuracy(goal.id, prog.accuracy - 5)}
                                className={`w-11 h-11 rounded-xl font-bold text-base flex items-center justify-center border active:scale-95 cursor-pointer ${
                                  isLightMobile
                                    ? 'bg-white hover:bg-slate-100 text-slate-800 border-slate-200 shadow-xs'
                                    : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-700'
                                }`}
                              >
                                -5%
                              </button>
                              <div className="flex-1 flex gap-1 overflow-x-auto py-1">
                                {ACCURACY_PRESETS.map((p) => (
                                  <button
                                    key={p}
                                    type="button"
                                    onClick={() => handleSetAccuracy(goal.id, p)}
                                    className={`flex-1 min-h-[44px] py-1.5 px-1 rounded-xl text-xs font-bold font-mono transition-all cursor-pointer ${
                                      prog.accuracy === p
                                        ? 'bg-cyan-600 text-white shadow-xs'
                                        : isLightMobile
                                        ? 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 shadow-xs'
                                        : 'bg-slate-800/80 text-slate-300 hover:bg-slate-800 border border-slate-700/60'
                                    }`}
                                  >
                                    {p}%
                                  </button>
                                ))}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleSetAccuracy(goal.id, prog.accuracy + 5)}
                                className={`w-11 h-11 rounded-xl font-bold text-base flex items-center justify-center border active:scale-95 cursor-pointer ${
                                  isLightMobile
                                    ? 'bg-white hover:bg-slate-100 text-slate-800 border-slate-200 shadow-xs'
                                    : 'bg-slate-800 hover:bg-slate-700 text-white border-slate-700'
                                }`}
                              >
                                +5%
                              </button>
                            </div>
                          </div>

                          {/* Cueing Level Chips */}
                          <div>
                            <span className={`block text-xs font-semibold mb-1.5 ${isLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                              Support / Cueing Level:
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {CUEING_OPTIONS.map((cue) => (
                                <button
                                  key={cue}
                                  type="button"
                                  onClick={() => handleSetCueing(goal.id, cue)}
                                  className={`min-h-[40px] px-3 py-1.5 rounded-xl text-xs font-medium transition-all cursor-pointer ${
                                    prog.cueing === cue
                                      ? isLightMobile
                                        ? 'bg-cyan-100 text-cyan-900 border border-cyan-300 font-bold'
                                        : 'bg-sky-500/20 text-sky-300 border border-sky-500/40 font-bold'
                                      : isLightMobile
                                      ? 'bg-white hover:bg-slate-50 text-slate-600 border border-slate-200 shadow-xs'
                                      : 'bg-slate-800 text-slate-400 hover:text-slate-200 border border-slate-700/60'
                                  }`}
                                >
                                  {cue}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Quick Evidence Note */}
                          <div>
                            <input
                              type="text"
                              value={prog.evidenceNote}
                              onChange={(e) => handleSetEvidence(goal.id, e.target.value)}
                              placeholder="Trial evidence, e.g. 8/10 correct in structured phrase"
                              className={`w-full rounded-xl px-3 py-2 text-xs min-h-[42px] focus:outline-none ${
                                isLightMobile
                                  ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20 shadow-xs'
                                  : 'bg-[#121a29]/90 border border-slate-700/80 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30'
                              }`}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className={`border-t ${isLightMobile ? 'border-slate-100' : 'border-slate-800/80'}`} />

          {/* 4. Additional Objective Notes (Optional) */}
          <div
            id="mobile-soap-objective-section"
            className={`space-y-2.5 transition-all ${
              highlightedField === 'objective' ? 'p-1.5 ring-1 ring-amber-400/50 rounded-xl bg-amber-50' : ''
            }`}
          >
            <div className="flex items-center justify-center space-x-1.5">
              <label htmlFor="mobile-soap-objective" className={`block text-xs font-bold uppercase tracking-wider text-center ${
                isLightMobile ? 'text-slate-800' : 'text-slate-300'
              }`}>
                Additional Objective Notes
              </label>
              <span className={`text-[11px] font-normal ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                (Optional)
              </span>
            </div>
            <textarea
              id="mobile-soap-objective"
              rows={2}
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              className={`w-full rounded-xl p-3 text-xs sm:text-sm leading-relaxed font-sans focus:outline-none ${
                isLightMobile
                  ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/20'
                  : 'bg-[#0a101b] border border-slate-700/80 focus:border-cyan-500 text-slate-100 placeholder-slate-500 focus:ring-1 focus:ring-cyan-500/30'
              }`}
            />
          </div>

          <div className={`border-t ${isLightMobile ? 'border-slate-100' : 'border-slate-800/80'}`} />

          <div className="flex flex-col items-center justify-center gap-2.5 pt-1">
            {/* Canonical SOAP Review (When expanded) */}
            {false && showSoapReview && (
              <div className={`w-full mt-3 rounded-2xl p-4 sm:p-5 space-y-4 text-left border ${
                isLightMobile
                  ? 'bg-slate-50 border-slate-200 shadow-xs'
                  : 'bg-[#0a101b] border-slate-700/80 shadow-inner'
              }`}>
                <div className={`flex items-center justify-between border-b pb-3 ${
                  isLightMobile ? 'border-slate-200' : 'border-slate-800'
                }`}>
                  <div>
                    <h3 className={`text-sm font-bold flex items-center space-x-2 ${
                      isLightMobile ? 'text-slate-900' : 'text-white'
                    }`}>
                      <Check className="w-4 h-4 text-emerald-600" />
                      <span>Canonical SOAP Review</span>
                    </h3>
                    <p className={`text-[11px] mt-0.5 ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                      Review and edit the canonical sections below before final signature and submission.
                    </p>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-semibold border ${
                    isLightMobile
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
                  }`}>
                    Ready for Review
                  </span>
                </div>

                <div className="space-y-3.5">
                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <label className={`block text-[11px] font-bold uppercase tracking-wider ${
                        isLightMobile ? 'text-slate-800' : 'text-slate-300'
                      }`}>
                        Subjective (S)
                      </label>
                      <span className={`text-[10px] font-mono ${isLightMobile ? 'text-slate-500' : 'text-slate-500'}`}>Presentation & Caregiver Reports</span>
                    </div>
                    <textarea
                      rows={2}
                      value={subjective}
                      onChange={(e) => setSubjective(e.target.value)}
                      placeholder="Enter client state, behavioral presentation, or caregiver report"
                      className={`w-full rounded-xl p-3 text-xs leading-relaxed font-sans focus:outline-none ${
                        isLightMobile
                          ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                          : 'bg-[#121a29] border border-slate-700/80 focus:border-cyan-500 text-slate-100 placeholder-slate-500 focus:ring-1 focus:ring-cyan-500/30'
                      }`}
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <label className={`block text-[11px] font-bold uppercase tracking-wider ${
                        isLightMobile ? 'text-slate-800' : 'text-slate-300'
                      }`}>
                        Objective (O)
                      </label>
                      <span className={`text-[10px] font-mono ${isLightMobile ? 'text-slate-500' : 'text-slate-500'}`}>Synthesized Goal Performance</span>
                    </div>
                    <textarea
                      rows={3}
                      value={objective}
                      onChange={(e) => setObjective(e.target.value)}
                      placeholder="Enter measurable performance data, goal accuracy, and cueing levels"
                      className={`w-full rounded-xl p-3 text-xs leading-relaxed font-sans focus:outline-none ${
                        isLightMobile
                          ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                          : 'bg-[#121a29] border border-slate-700/80 focus:border-cyan-500 text-slate-100 placeholder-slate-500 focus:ring-1 focus:ring-cyan-500/30'
                      }`}
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <label className={`block text-[11px] font-bold uppercase tracking-wider ${
                        isLightMobile ? 'text-slate-800' : 'text-slate-300'
                      }`}>
                        Assessment (A)
                      </label>
                      <span className={`text-[10px] font-mono ${isLightMobile ? 'text-slate-500' : 'text-slate-500'}`}>Clinical Analysis & Progress Response</span>
                    </div>
                    <textarea
                      rows={2}
                      value={assessment}
                      onChange={(e) => setAssessment(e.target.value)}
                      placeholder="Enter clinical assessment, response to intervention, and progress analysis"
                      className={`w-full rounded-xl p-3 text-xs leading-relaxed font-sans focus:outline-none ${
                        isLightMobile
                          ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                          : 'bg-[#121a29] border border-slate-700/80 focus:border-cyan-500 text-slate-100 placeholder-slate-500 focus:ring-1 focus:ring-cyan-500/30'
                      }`}
                    />
                  </div>

                  <div className="space-y-1">
                    <div className="flex justify-between items-center">
                      <label className={`block text-[11px] font-bold uppercase tracking-wider ${
                        isLightMobile ? 'text-slate-800' : 'text-slate-300'
                      }`}>
                        Plan (P)
                      </label>
                      <span className={`text-[10px] font-mono ${isLightMobile ? 'text-slate-500' : 'text-slate-500'}`}>Continuity & Carryover</span>
                    </div>
                    <textarea
                      rows={2}
                      value={plan}
                      onChange={(e) => setPlan(e.target.value)}
                      placeholder="Enter plan for upcoming sessions, target modifications, and caregiver strategies"
                      className={`w-full rounded-xl p-3 text-xs leading-relaxed font-sans focus:outline-none ${
                        isLightMobile
                          ? 'bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-cyan-500'
                          : 'bg-[#121a29] border border-slate-700/80 focus:border-cyan-500 text-slate-100 placeholder-slate-500 focus:ring-1 focus:ring-cyan-500/30'
                      }`}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Guardian / Designee Signature */}
        <div id="mobile-soap-guardian-section" className={`rounded-2xl p-5 space-y-4 transition-all border ${
          highlightedField === 'guardian'
            ? 'border-amber-400 ring-2 ring-amber-400/40 bg-amber-50'
            : isLightMobile
            ? 'bg-white border-slate-200/90 shadow-xs'
            : 'bg-gradient-to-b from-[#162438] via-[#121c2e] to-[#0e1625] border-slate-700/70 shadow-md'
        }`}>
          <div className="flex items-center justify-center">
            <h3 className={`text-sm font-bold flex items-center space-x-2 text-center ${
              isLightMobile ? 'text-slate-900' : 'text-white'
            }`}>
              <PenTool className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Guardian / Designee Signature</span>
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={`block text-[11px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>
                Signer Name
              </label>
              <input
                type="text"
                value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                placeholder="Parent / Guardian Name"
                className={`w-full rounded-xl px-3 py-2.5 text-xs min-h-[44px] focus:outline-none ${
                  isLightMobile
                    ? 'bg-slate-50 border border-slate-200 text-slate-900 placeholder:text-slate-400 focus:bg-white focus:border-cyan-500'
                    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 placeholder-slate-500 focus:border-sky-500'
                }`}
              />
            </div>

            <div>
              <label className={`block text-[11px] font-semibold mb-1 ${isLightMobile ? 'text-slate-600' : 'text-slate-400'}`}>
                Relationship to Client
              </label>
              <select
                value={guardianRelationship}
                onChange={(e) => setGuardianRelationship(e.target.value)}
                className={`w-full rounded-xl px-3 py-2.5 text-xs min-h-[44px] focus:outline-none ${
                  isLightMobile
                    ? 'bg-slate-50 border border-slate-200 text-slate-900 focus:bg-white focus:border-cyan-500'
                    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100 focus:border-sky-500'
                }`}
              >
                <option value="Parent / Legal Guardian">Parent / Legal Guardian</option>
                <option value="Foster Parent">Foster Parent</option>
                <option value="Authorized Designee">Authorized Designee</option>
                <option value="Client / Self">Client / Self</option>
              </select>
            </div>
          </div>

          <p className={`text-xs leading-relaxed ${isLightMobile ? 'text-slate-700' : 'text-slate-200'}`}>
            {requiresOnsiteGuardianPad ? 'The named signer must sign this pad themselves. Do not sign, initial, or draw a line for them. ' : ''}
            {inPersonEncounterAcknowledgment({ date: dos, timeIn: timeInHhMm, timeOut: timeOutHhMm })}
          </p>

          {serviceSetting !== 'TELETHERAPY' && approvedRemoteAssignment && (
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setGuardianSignatureChoice('IN_PERSON')}
                className={`rounded-lg px-2 py-2 text-[11px] font-semibold border ${
                  guardianSignatureChoice !== 'REMOTE'
                    ? 'bg-emerald-600 text-white border-emerald-600'
                    : isLightMobile
                      ? 'bg-white text-slate-700 border-slate-300'
                      : 'bg-transparent text-slate-200 border-slate-600'
                }`}
              >
                Guardian signs here
              </button>
              <button
                type="button"
                onClick={() => setGuardianSignatureChoice('REMOTE')}
                className={`rounded-lg px-2 py-2 text-[11px] font-semibold border ${
                  guardianSignatureChoice === 'REMOTE'
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : isLightMobile
                      ? 'bg-white text-slate-700 border-slate-300'
                      : 'bg-transparent text-slate-200 border-slate-600'
                }`}
              >
                Remote signature via SignWell
              </button>
            </div>
          )}

          {requiresOnsiteGuardianPad ? (
            <div className="space-y-2">
              <div className="flex items-center justify-end text-[11px]">
                <button
                  type="button"
                  onClick={clearSignature}
                  className={`font-semibold cursor-pointer ${
                    isLightMobile ? 'text-cyan-700 hover:text-cyan-900 underline' : 'text-sky-400 hover:text-sky-300 underline'
                  }`}
                >
                  Clear Pad
                </button>
              </div>

              <div className="border border-slate-300 bg-white rounded-xl overflow-hidden touch-none shadow-xs">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={130}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  className="w-full h-32 cursor-crosshair bg-white"
                />
              </div>
              {!hasGuardianSignatureEvidence && (
                <p className={`text-xs font-medium ${isLightMobile ? 'text-amber-800' : 'text-amber-200'}`}>
                  Guardian/designee signature is required before this session note can be submitted.
                </p>
              )}
            </div>
          ) : (
            <p className={`text-xs leading-relaxed ${isLightMobile ? 'text-slate-600' : 'text-slate-300'}`}>
              {serviceSetting === 'TELETHERAPY'
                ? 'This teletherapy note can be saved with the narrative, goal performance, and clinician attestation. The guardian signature is requested afterward with Send to Guardian for eSign.'
                : 'This note can be saved with the narrative, goal performance, and clinician attestation. The guardian signature is requested afterward with Send to Guardian for eSign.'}
            </p>
          )}
        </div>

        {/* Treating Clinician & Submit */}
        <div id="mobile-soap-attestation-section" className={`rounded-2xl p-5 space-y-4 transition-all border ${
          highlightedField === 'attestation'
            ? 'border-amber-400 ring-2 ring-amber-400/40 bg-amber-50'
            : isLightMobile
            ? 'bg-white border-slate-200/90 shadow-xs'
            : 'bg-gradient-to-b from-[#162438] via-[#121c2e] to-[#0e1625] border-slate-700/70 shadow-md'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <UserCheck className={`w-4 h-4 ${isLightMobile ? 'text-cyan-700' : 'text-sky-400'}`} />
              <span className={`text-xs font-bold ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>Treating Clinician:</span>
              <span className={`text-xs font-semibold ${
                isEncounterProviderResolved
                  ? isLightMobile ? 'text-slate-800' : 'text-slate-200'
                  : 'text-amber-600 italic'
              }`}>
                {canonicalClinicianName || 'Identity Unverified'}
              </span>
            </div>
            {isEncounterProviderResolved && (
              <span
                className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded border ${
                  isEncounterSlpa
                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                    : 'bg-emerald-50 text-emerald-800 border-emerald-200'
                }`}
              >
                {isEncounterSlpa ? 'SLPA (Requires Supervisor Co-sign)' : 'SLP (Direct Sign)'}
              </span>
            )}
          </div>

          {!isEncounterProviderResolved && (
            <div className={`text-xs p-3.5 rounded-xl flex items-start space-x-2.5 border ${
              isLightMobile
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'text-amber-300 bg-amber-950/40 border-amber-500/40'
            }`}>
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isLightMobile ? 'text-amber-600' : 'text-amber-400'}`} />
              <div className="space-y-1">
                <div className={`font-bold ${isLightMobile ? 'text-amber-900' : 'text-amber-200'}`}>Treating Clinician Verification Required</div>
                <div className={`leading-relaxed ${isLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                  Treating clinician identity could not be verified. Please contact the office before submitting this note.
                </div>
              </div>
            </div>
          )}

          {isEncounterProviderResolved && isEncounterSlpa && (
            <div className={`text-xs p-3 rounded-xl flex items-start space-x-2 border ${
              isLightMobile
                ? 'bg-amber-50 text-amber-900 border-amber-200'
                : 'text-amber-400/90 bg-amber-500/10 border-amber-500/20'
            }`}>
              <ShieldCheck className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isLightMobile ? 'text-amber-600' : ''}`} />
              <span>
                As an SLPA session, submitting will record your clinical signature and automatically route this note to your designated Clinical Supervisor for co-signature (Status:{' '}
                <span className="font-mono font-bold">PENDING_SUPERVISION</span>).
              </span>
            </div>
          )}

          {isEncounterProviderResolved && isTreatingProviderMismatch && (
            <div className={`text-xs p-3.5 rounded-xl flex items-start space-x-2.5 border ${
              isLightMobile
                ? 'bg-rose-50 border-rose-200 text-rose-900'
                : 'text-rose-300 bg-rose-950/40 border-rose-500/40'
            }`}>
              <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isLightMobile ? 'text-rose-600' : 'text-rose-400'}`} />
              <div className="space-y-1">
                <div className={`font-bold ${isLightMobile ? 'text-rose-900' : 'text-rose-200'}`}>Clinician Signature Authorization Restricted</div>
                <div className={`leading-relaxed ${isLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
                  Authenticated user is <span className={`font-semibold ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>{user.name}</span> ({user.role}), but this encounter is assigned to treating clinician <strong className={isLightMobile ? 'text-slate-900' : 'text-sky-300'}>{canonicalClinicianName}</strong>. Clinical notes can only be signed by the canonical treating provider.
                </div>
              </div>
            </div>
          )}

          {/* Explicit Clinician Attestation Checkbox */}
          <div className={`border rounded-xl p-3.5 flex items-start space-x-3 transition-colors ${
            highlightedField === 'attestation' ? 'border-amber-400 ring-2 ring-amber-400/40 bg-amber-50' : ''
          } ${
            isLightMobile ? 'bg-slate-50 border-slate-200 text-slate-800' : 'bg-[#0a101b] border-slate-700/80 text-slate-100'
          }`}>
            <input
              type="checkbox"
              id="clinicianAttestationCheckbox"
              checked={clinicianAttestationChecked}
              onChange={(e) => setClinicianAttestationChecked(e.target.checked)}
              className={`w-5 h-5 rounded mt-0.5 cursor-pointer ${
                isLightMobile
                  ? 'border-slate-300 text-emerald-600 focus:ring-emerald-500'
                  : 'bg-[#162032] border-slate-600 text-emerald-500 focus:ring-0'
              }`}
            />
            <label htmlFor="clinicianAttestationCheckbox" className={`text-xs font-medium cursor-pointer select-none leading-relaxed ${
              isLightMobile ? 'text-slate-800' : 'text-slate-100'
            }`}>
              I attest that this note accurately reflects skilled clinical services personally rendered by <strong className={isLightMobile ? 'text-slate-900' : 'text-white'}>{canonicalClinicianName || 'the assigned treating clinician'}</strong> on <span className={`font-mono ${isLightMobile ? 'text-cyan-800 font-semibold' : 'text-sky-300'}`}>{dos}</span> per the established plan of care.
            </label>
          </div>

          {/* Validation Alert Banner above Submit Button */}
          {validationNotice && (
            <div className={`border rounded-xl p-3 flex items-center justify-between gap-3 ${
              isLightMobile
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-amber-950/60 border-amber-500/60 text-amber-200'
            }`}>
              <div className="flex items-center space-x-2">
                <AlertTriangle className={`w-4 h-4 shrink-0 ${isLightMobile ? 'text-amber-600' : 'text-amber-400'}`} />
                <span className={`text-xs font-medium ${isLightMobile ? 'text-amber-900' : 'text-amber-200'}`}>
                  <strong className={`font-bold mr-1 ${isLightMobile ? 'text-amber-950' : 'text-amber-300'}`}>Required before signing:</strong>
                  {validationNotice.message} Complete the highlighted field before submitting.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  const targetId =
                    validationNotice.field === 'narrative' ? 'mobile-soap-narrative-section' :
                    validationNotice.field === 'goals' ? 'mobile-soap-goals-section' :
                    validationNotice.field === 'objective' ? 'mobile-soap-objective-section' :
                    validationNotice.field === 'guardian' ? 'mobile-soap-guardian-section' :
                    validationNotice.field === 'attestation' ? 'mobile-soap-attestation-section' :
                    validationNotice.field === 'makeup' ? 'mobile-soap-makeup-section' :
                    validationNotice.field === 'setting' ? 'mobile-soap-setting-section' :
                    validationNotice.field === 'clients_treated_together' ? 'mobile-soap-group-section' :
                    'mobile-soap-context-section';
                  const el = document.getElementById(targetId);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    const interactiveEl = el.querySelector('input, textarea, select') as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
                    if (interactiveEl) interactiveEl.focus();
                  }
                }}
                className={`px-2.5 py-1 text-[10px] font-bold border rounded transition-colors cursor-pointer shrink-0 ${
                  isLightMobile
                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border-amber-300'
                    : 'bg-amber-500/20 hover:bg-amber-500/30 border-amber-500/40 text-amber-300'
                }`}
              >
                Complete Field
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmitSession}
            disabled={isSubmitting || !isEncounterProviderResolved || isTreatingProviderMismatch || (requiresOnsiteGuardianPad && !hasGuardianSignatureEvidence)}
            className={`w-full py-4 text-white font-bold rounded-2xl text-sm flex items-center justify-center space-x-2 min-h-[52px] disabled:opacity-50 active:scale-[0.99] transition-all cursor-pointer ${
              isLightMobile
                ? 'bg-emerald-600 hover:bg-emerald-700 shadow-md shadow-emerald-700/20'
                : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-xl shadow-emerald-600/20'
            }`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Signing & Submitting Note...</span>
              </>
            ) : !isEncounterProviderResolved ? (
              <>
                <ShieldAlert className="w-5 h-5 text-amber-200" />
                <span>Treating Clinician Identity Unverified</span>
              </>
            ) : isTreatingProviderMismatch ? (
              <>
                <ShieldAlert className="w-5 h-5 text-rose-200" />
                <span>Treating Clinician Signature Required ({canonicalClinicianName})</span>
              </>
            ) : (requiresOnsiteGuardianPad && !hasGuardianSignatureEvidence) ? (
              <>
                <ShieldAlert className="w-5 h-5 text-amber-200" />
                <span>Guardian/designee signature is required before this session note can be submitted.</span>
              </>
            ) : (isUnder53Minutes && !isMakeupVisit) ? (
              <>
                <Clock className="w-5 h-5 text-amber-200" />
                <span>Minimum 53 Min Required ({elapsedMinutes}m / 53m)</span>
              </>
            ) : (
              <>
                <FileCheck className="w-5 h-5" />
                <span>Sign & Submit Session Note</span>
              </>
            )}
          </button>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setShowDiscardModal(true)}
              className={`text-xs font-medium transition-colors py-2 px-3 rounded-lg inline-flex items-center space-x-1.5 cursor-pointer ${
                isLightMobile
                  ? 'text-slate-500 hover:text-rose-600 hover:bg-slate-100'
                  : 'text-slate-500 hover:text-rose-400 hover:bg-slate-900'
              }`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Accidental start? Discard / Cancel this unsigned session</span>
            </button>
          </div>
        </div>

        {/* Discard Confirmation Modal (Session View) */}
        {showDiscardModal && selectedClient && (
          <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xs ${
            isLightMobile ? 'bg-slate-900/60' : 'bg-black/80'
          }`}>
            <div className={`max-w-md w-full rounded-2xl p-6 shadow-2xl space-y-4 border ${
              isLightMobile ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3 text-rose-500">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                    isLightMobile ? 'bg-rose-50 border-rose-200' : 'bg-rose-500/20 border-rose-500/30'
                  }`}>
                    <Trash2 className="w-5 h-5 text-rose-600" />
                  </div>
                  <div>
                    <h3 className={`text-base font-bold ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>Discard this unsigned session?</h3>
                    <p className={`text-xs ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                      {selectedClient.client_first_name} {selectedClient.client_last_name} • {elapsedMinutes} min
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDiscardModal(false)}
                  className={`p-1 rounded-lg ${isLightMobile ? 'text-slate-400 hover:text-slate-700' : 'text-slate-500 hover:text-slate-300'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <p className={`text-xs leading-relaxed p-3 rounded-xl border ${
                isLightMobile ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-slate-950/60 border-slate-800/80 text-slate-300'
              }`}>
                Discarding will remove only this unsigned in-progress session draft. No completed service, clinical note, or billing-ready record will be created.
              </p>

              <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDiscardModal(false)}
                  className={`flex-1 py-3 font-semibold rounded-xl text-xs transition-colors min-h-[44px] cursor-pointer ${
                    isLightMobile ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                  }`}
                >
                  Keep Editing
                </button>
                <button
                  type="button"
                  onClick={handleDiscardSession}
                  className="flex-1 py-3 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-xl text-xs shadow-md shadow-rose-600/20 transition-colors min-h-[44px] flex items-center justify-center space-x-1.5 cursor-pointer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  <span>Discard Session</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ==========================================
  // RENDER: MY CASELOAD LANDING (MOBILE FIRST)
  // ==========================================
  const isAssistant = user.role === 'ASSISTANT';
  const isSupervisor = user.role === 'SUPERVISOR';
  const isLightMobile = isAssistant || isSupervisor;
  const roleBadgeText = isAssistant ? 'SLPA' : 'Supervisor';

  return (
    <div className="max-w-2xl mx-auto space-y-2.5 pb-20">
      {/* Header — Canonical Bloom Rich Slate-Blue / Navy Structural Header on Mobile & Desktop */}
      <div className="relative overflow-hidden rounded-2xl border border-slate-700/80 mobile-header-shell md:desktop-header-shell px-4 py-3 sm:px-5 sm:py-3.5 shadow-xl md:shadow-[0_4px_16px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.12)]">
        <div className="absolute top-0 right-0 w-96 h-full bg-gradient-to-l from-sky-500/5 via-indigo-500/5 to-transparent pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center justify-center text-center w-full space-y-1.5">
          {/* Centered Title */}
          <h2 className="text-xl font-bold tracking-tight text-white">
            My Caseload
          </h2>

          {/* Centered Metadata Badges beneath */}
          <div className="flex items-center justify-center space-x-1.5">
            <span className="text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full bg-slate-800/90 text-slate-200 border border-slate-700/80 inline-block shadow-2xs">
              {filteredAssignments.length} Clients
            </span>
            <span className="text-[11px] font-mono font-semibold px-2.5 py-0.5 rounded-full border border-slate-700/80 bg-slate-800/90 text-slate-200 leading-none inline-block shadow-2xs">
              {roleBadgeText}
            </span>
          </div>
        </div>
      </div>

      {/* Active Session in Progress Banner (if session is active and user backed out to caseload) */}
      {selectedClient && (
        <div className={`rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 ${
          isLightMobile
            ? 'bg-white border border-emerald-200/90 shadow-xs'
            : 'bg-gradient-to-r from-[#1a3356] via-[#152742] to-[#101e33] border border-slate-600/80 shadow-xl'
        }`}>
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
              isLightMobile
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-slate-800/80 border-slate-600 text-slate-200'
            }`}>
              <Clock className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                <span className={`inline-flex items-center gap-1.5 text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                  isLightMobile
                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                    : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                }`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  LIVE SESSION IN PROGRESS
                </span>
                <span className={`text-xs font-mono ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>Started: {startTimeStr || 'Today'}</span>
                {isAuthOverride && (
                  <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded leading-none whitespace-nowrap ${
                    isLightMobile
                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                      : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  }`}>
                    AUTH OVERRIDE
                  </span>
                )}
              </div>
              <div className={`text-sm font-bold mt-0.5 ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>
                {selectedClient.client_last_name}, {selectedClient.client_first_name}
              </div>
              <div className={`text-xs ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                {timeInHhMm || startTimeStr || 'In progress'}{timeOutHhMm ? `–${timeOutHhMm}` : ''} • {elapsedMinutes} min
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2 w-full sm:w-auto justify-end pt-1 sm:pt-0">
            <button
              type="button"
              onClick={() => setShowDiscardModal(true)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-colors flex items-center space-x-1 min-h-[40px] cursor-pointer ${
                isLightMobile
                  ? 'bg-slate-100 hover:bg-rose-50 text-slate-600 hover:text-rose-700 border border-slate-200'
                  : 'bg-slate-800 hover:bg-rose-950/40 text-slate-400 hover:text-rose-300 border border-slate-700 hover:border-rose-500/40'
              }`}
              title="Discard this unsigned session"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span>Discard</span>
            </button>
            <button
              type="button"
              onClick={handleResumeSession}
              className={`px-4 py-2 font-semibold rounded-xl text-xs flex items-center space-x-1.5 transition-colors min-h-[40px] active:scale-98 cursor-pointer ${
                isLightMobile
                  ? 'bg-slate-900 hover:bg-slate-800 text-white border border-slate-700 shadow-xs'
                  : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 shadow-xs'
              }`}
            >
              <Play className="w-3.5 h-3.5 fill-current text-white shrink-0" />
              <span className="tracking-tight">Resume Session</span>
            </button>
          </div>
        </div>
      )}

      {/* Scoped Local Search Surface */}
      <div className="relative">
        <div className={`relative flex items-center gap-2 px-3 py-2 rounded-xl border transition-all min-h-[44px] ${
          isLightMobile
            ? 'bg-white border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.04)] focus-within:border-slate-400 focus-within:ring-1 focus-within:ring-slate-400/20'
            : 'border-slate-700/80 bg-gradient-to-b from-[#182436] to-[#101826] shadow-[0_2px_8px_rgba(0,0,0,0.35),inset_0_1px_0_0_rgba(255,255,255,0.06)] focus-within:border-sky-400/70 focus-within:ring-1 focus-within:ring-sky-500/25'
        }`}>
          {!isLightMobile && (
            <div className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-slate-300/10 to-transparent pointer-events-none" />
          )}

          {/* Standard unbranded Search icon */}
          <div className={`flex items-center pl-0.5 shrink-0 select-none ${isLightMobile ? 'text-slate-400' : 'text-slate-400'}`}>
            <Search className="w-3.5 h-3.5 shrink-0" />
          </div>

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search clients..."
            className={`w-full bg-transparent text-xs tracking-tight font-medium focus:outline-none ${
              isLightMobile
                ? 'text-slate-900 placeholder:text-slate-400'
                : 'text-white placeholder:text-slate-500'
            }`}
          />

          {/* Clear button */}
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className={`p-1 rounded transition-colors shrink-0 ${isLightMobile ? 'text-slate-400 hover:text-slate-700' : 'text-slate-500 hover:text-slate-300'}`}
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Caseload Cards */}
      {loadingCaseload ? (
        <div className={`py-16 text-center text-xs flex flex-col items-center justify-center space-y-2 ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
          <Loader2 className={`w-6 h-6 animate-spin ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`} />
          <span>Loading authorized caseload...</span>
        </div>
      ) : filteredAssignments.length === 0 ? (
        <div className={`rounded-2xl p-8 text-center space-y-3 border ${
          isLightMobile
            ? 'bg-white border-slate-200/90 shadow-xs'
            : 'bg-slate-900/60 border-slate-800'
        }`}>
          <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${
            isLightMobile ? 'bg-slate-100 text-slate-500' : 'bg-slate-800 text-slate-400'
          }`}>
            <Calendar className="w-6 h-6" />
          </div>
          <div className={`text-sm font-semibold ${isLightMobile ? 'text-slate-900' : 'text-slate-200'}`}>No Assigned Clients Found</div>
          <p className={`text-xs max-w-sm mx-auto ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
            {searchQuery
              ? 'No clients match your search.'
              : 'You do not have any active client assignments yet. Please contact your administrator.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredAssignments.map((client, index) => {
            const weeklyCount = calculateWeeklyVisits(client.client_id);
            const freqStr = client.auth_frequency || client.expected_frequency || '2x/week';
            const freqMatch = freqStr.match(/(\d+)/);
            const targetWeekly = freqMatch ? parseInt(freqMatch[1], 10) : 2;

            // Rotating distinct non-primary accent color theme per client
            const theme = CLIENT_ACCENT_PALETTE[index % CLIENT_ACCENT_PALETTE.length];

            const isFeeding = client.auth_service_code === '92526' || (client as any).service_type?.toLowerCase()?.includes('feed');
            const isClientActive = selectedClient && selectedClient.client_id === client.client_id;
            const authUnitsLabel = caseloadUnitsLabel(client);
            const showNoActiveAuth = caseloadShowsNoActiveAuth(client);
            const showAuthReview = caseloadNeedsAuthReview(client);

            return (
              <div
                key={client.client_id}
                className={`relative rounded-xl border transition-all duration-150 px-3 py-2 flex items-center justify-between gap-2 overflow-hidden border-l-[3px] ${
                  isLightMobile
                    ? isClientActive
                      ? 'bg-emerald-50/70 border-emerald-200 border-l-emerald-600 shadow-xs'
                      : 'bg-white hover:bg-slate-50/80 border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.04)] border-l-slate-400'
                    : isClientActive
                    ? 'bg-gradient-to-r from-[#203656] via-[#182b45] to-[#132237] border-sky-400/60 border-l-sky-400 ring-1 ring-sky-400/25 shadow-[0_2px_10px_rgba(0,0,0,0.4),inset_0_1px_0_0_rgba(255,255,255,0.08)]'
                    : `${theme.leftBorder} ${theme.rowHalo} ${
                        index % 2 === 0
                          ? 'bg-gradient-to-r from-[#1e2f4a] via-[#17253c] to-[#131f32] border-slate-700/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_2px_8px_rgba(0,0,0,0.3)]'
                          : 'bg-gradient-to-r from-[#243756] via-[#1c2c46] to-[#162337] border-slate-700/70 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06),0_2px_8px_rgba(0,0,0,0.3)]'
                      }`
                }`}
                aria-label={`Client card for ${client.client_last_name}, ${client.client_first_name}`}
              >
                {!isLightMobile && (
                  <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
                )}

                {/* Left: Client Name + Badges + Weekly Progress */}
                <div className="min-w-0 flex-1 pr-1.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`font-bold text-[13.5px] tracking-tight truncate ${
                      isLightMobile ? 'text-slate-900' : 'text-white'
                    }`}>
                      {client.client_last_name}, {client.client_first_name}
                    </span>
                    {isClientActive && (
                      <span className={`inline-flex items-center gap-1 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-full leading-none shrink-0 ${
                        isLightMobile
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                          : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                      }`}>
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        LIVE
                      </span>
                    )}
                    {showNoActiveAuth && (
                      <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 rounded leading-none whitespace-nowrap shrink-0 ${
                        isLightMobile
                          ? 'bg-amber-50 text-amber-800 border border-amber-200'
                          : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                      }`}>
                        {isClientActive && isAuthOverride ? 'NO ACTIVE AUTH (OVERRIDE)' : 'NO ACTIVE AUTH'}
                      </span>
                    )}
                    {showAuthReview && (
                      <span className={`text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded leading-none whitespace-nowrap shrink-0 border ${
                        isLightMobile
                          ? 'bg-slate-50 text-slate-600 border-slate-300'
                          : 'text-slate-300 border-slate-600'
                      }`}>
                        AUTH REVIEW
                      </span>
                    )}
                  </div>

                  <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <span className={`text-[11px] font-mono font-semibold whitespace-nowrap leading-none ${
                      isLightMobile
                        ? weeklyCount > targetWeekly
                          ? 'text-rose-700 font-bold'
                          : weeklyCount === targetWeekly
                          ? 'text-emerald-700'
                          : weeklyCount > 0
                          ? 'text-amber-700'
                          : 'text-slate-500'
                        : weeklyCount > targetWeekly
                        ? 'text-rose-300 font-bold'
                        : weeklyCount === targetWeekly
                        ? 'text-emerald-300'
                        : weeklyCount > 0
                        ? 'text-amber-300'
                        : 'text-slate-400'
                    }`}>
                      {weeklyCount} of {targetWeekly} this week
                    </span>
                    {authUnitsLabel && (
                      <span className={`text-[11px] font-mono font-semibold whitespace-nowrap leading-none ${
                        isLightMobile ? 'text-slate-600' : 'text-slate-300'
                      }`}>
                        {authUnitsLabel}
                      </span>
                    )}
                    {weeklyCount > targetWeekly && (
                      <span className={`text-[8.5px] font-mono font-bold px-1 py-0.2 rounded uppercase tracking-wider leading-none ${
                        isLightMobile
                          ? 'text-rose-700 bg-rose-50 border border-rose-200'
                          : 'text-rose-400 bg-rose-950/70 border border-rose-500/40'
                      }`}>
                        Above
                      </span>
                    )}
                  </div>
                </div>

                {/* Right: Action Button */}
                {isClientActive ? (
                  <div className="flex items-center space-x-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={handleResumeSession}
                      className={`px-2.5 sm:px-3 py-1.5 font-semibold rounded-lg text-xs flex items-center space-x-1.5 transition-colors cursor-pointer active:scale-98 ${
                        isLightMobile
                          ? 'bg-slate-900 hover:bg-slate-800 text-white border border-slate-700 shadow-xs'
                          : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 shadow-xs'
                      }`}
                    >
                      <Play className="w-3 h-3 fill-current text-white shrink-0" />
                      <span className="whitespace-nowrap tracking-tight">Resume</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowDiscardModal(true)}
                      className={`p-1.5 rounded-lg text-xs transition-colors cursor-pointer ${
                        isLightMobile
                          ? 'bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-700 border border-slate-200'
                          : 'bg-slate-800 hover:bg-rose-950/50 text-slate-400 hover:text-rose-300 border border-slate-700 hover:border-rose-500/40'
                      }`}
                      title="Discard in-progress session"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ) : (
                  <StartSpeechButton
                    onClick={() => handleStartSession(client)}
                    isFeeding={isFeeding}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Discard Confirmation Modal (Caseload View) */}
      {showDiscardModal && selectedClient && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xs ${
          isLightMobile ? 'bg-slate-900/60' : 'bg-black/80'
        }`}>
          <div className={`max-w-md w-full rounded-2xl p-6 shadow-2xl space-y-4 border ${
            isLightMobile ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 text-rose-500">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                  isLightMobile ? 'bg-rose-50 border-rose-200' : 'bg-rose-500/20 border-rose-500/30'
                }`}>
                  <Trash2 className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h3 className={`text-base font-bold ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>Discard this unsigned session?</h3>
                  <p className={`text-xs ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                    {selectedClient.client_first_name} {selectedClient.client_last_name} • {elapsedMinutes} min
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDiscardModal(false)}
                className={`p-1 rounded-lg ${isLightMobile ? 'text-slate-400 hover:text-slate-700' : 'text-slate-500 hover:text-slate-300'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className={`text-xs leading-relaxed p-3 rounded-xl border ${
              isLightMobile ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-slate-950/60 border-slate-800/80 text-slate-300'
            }`}>
              Discarding will remove only this unsigned in-progress session draft. No completed service, clinical note, or billing-ready record will be created.
            </p>

            <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowDiscardModal(false)}
                className={`flex-1 py-3 font-semibold rounded-xl text-xs transition-colors min-h-[44px] cursor-pointer ${
                  isLightMobile ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200' : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                }`}
              >
                Keep Editing
              </button>
              <button
                type="button"
                onClick={handleDiscardSession}
                className="flex-1 py-3 bg-rose-600 hover:bg-rose-500 text-white font-bold rounded-xl text-xs shadow-md shadow-rose-600/20 transition-colors min-h-[44px] flex items-center justify-center space-x-1.5 cursor-pointer"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>Discard Session</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Session Conflict Modal */}
      {conflictClient && selectedClient && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xs ${
          isLightMobile ? 'bg-slate-900/60' : 'bg-black/80'
        }`}>
          <div className={`max-w-md w-full rounded-2xl p-6 shadow-2xl space-y-4 border ${
            isLightMobile ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'
          }`}>
            <div className="flex items-center space-x-3 text-amber-500">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                isLightMobile ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/20 border-amber-500/30'
              }`}>
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className={`text-base font-bold ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>Active Session in Progress</h3>
                <p className={`text-xs ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>Another client's session is currently open</p>
              </div>
            </div>

            <p className={`text-xs leading-relaxed ${isLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
              You currently have an active in-progress session for{' '}
              <strong className={isLightMobile ? 'text-slate-900' : 'text-white'}>
                {selectedClient.client_first_name} {selectedClient.client_last_name}
              </strong>{' '}
              ({elapsedMinutes} min). You can resume it, or discard it to start a session for{' '}
              <strong className={isLightMobile ? 'text-slate-900' : 'text-white'}>
                {conflictClient.client_first_name} {conflictClient.client_last_name}
              </strong>.
            </p>

            <div className="flex flex-col gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setConflictClient(null);
                  handleResumeSession();
                }}
                className={`w-full py-3 font-semibold rounded-xl text-xs flex items-center justify-center space-x-1.5 transition-colors min-h-[44px] active:scale-98 cursor-pointer ${
                  isLightMobile
                    ? 'bg-slate-900 hover:bg-slate-800 text-white border border-slate-700 shadow-xs'
                    : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-600 shadow-xs'
                }`}
              >
                <Play className="w-3.5 h-3.5 fill-current text-white shrink-0" />
                <span className="tracking-tight">Resume {selectedClient.client_first_name}'s Session</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  const next = conflictClient;
                  handleDiscardSession();
                  setConflictClient(null);
                  startSpeechSession(next);
                }}
                className={`w-full py-2.5 font-semibold rounded-xl text-xs transition-colors min-h-[44px] cursor-pointer ${
                  isLightMobile
                    ? 'bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200'
                    : 'bg-rose-950/40 hover:bg-rose-900/40 text-rose-300 border border-rose-500/30'
                }`}
              >
                Discard {selectedClient.client_first_name}'s Draft & Start {conflictClient.client_first_name}
              </button>

              <button
                type="button"
                onClick={() => setConflictClient(null)}
                className={`w-full py-2.5 rounded-xl text-xs font-semibold transition-colors min-h-[40px] cursor-pointer ${
                  isLightMobile
                    ? 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Above Expected Weekly Frequency Confirmation Modal */}
      {aboveFrequencyWarningClient && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xs ${
          isLightMobile ? 'bg-slate-900/60' : 'bg-black/80'
        }`}>
          <div className={`max-w-md w-full rounded-2xl p-6 shadow-2xl space-y-4 border ${
            isLightMobile ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 text-amber-500">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                  isLightMobile ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/20 border-amber-500/30'
                }`}>
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className={`text-base font-bold ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>Above expected weekly frequency</h3>
                  <p className={`text-xs ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                    {aboveFrequencyWarningClient.client_first_name} {aboveFrequencyWarningClient.client_last_name}
                  </p>
                </div>
              </div>
            </div>
            <p className={`text-xs leading-relaxed ${isLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
              This visit will exceed the client’s expected weekly treatment frequency.
            </p>
            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setAboveFrequencyWarningClient(null)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer border ${
                  isLightMobile ? 'border-slate-200 text-slate-700 hover:bg-slate-100' : 'border-slate-700 hover:bg-slate-800 text-slate-300'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const client = aboveFrequencyWarningClient;
                  setAboveFrequencyWarningClient(null);
                  startSpeechSession(client);
                }}
                className="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold transition-all shadow-md cursor-pointer"
              >
                Continue Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* No Active Authorization Warning Modal */}
      {noAuthWarningClient && (
        <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-xs ${
          isLightMobile ? 'bg-slate-900/60' : 'bg-black/80'
        }`}>
          <div className={`max-w-md w-full rounded-2xl p-6 shadow-2xl space-y-4 border ${
            isLightMobile ? 'bg-white border-slate-200' : 'bg-slate-900 border-slate-800'
          }`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3 text-amber-500">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                  isLightMobile ? 'bg-amber-50 border-amber-200' : 'bg-amber-500/20 border-amber-500/30'
                }`}>
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className={`text-base font-bold ${isLightMobile ? 'text-slate-900' : 'text-white'}`}>No active authorization</h3>
                  <p className={`text-xs ${isLightMobile ? 'text-slate-500' : 'text-slate-400'}`}>
                    {noAuthWarningClient.client_first_name} {noAuthWarningClient.client_last_name}
                  </p>
                </div>
              </div>
            </div>
            <p className={`text-xs leading-relaxed ${isLightMobile ? 'text-slate-700' : 'text-slate-300'}`}>
              No active authorization is currently available for this client. This session may not be billable.
            </p>
            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setNoAuthWarningClient(null)}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-colors cursor-pointer border ${
                  isLightMobile ? 'border-slate-200 text-slate-700 hover:bg-slate-100' : 'border-slate-700 hover:bg-slate-800 text-slate-300'
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  const client = noAuthWarningClient;
                  setNoAuthWarningClient(null);
                  if (client) {
                    handleStartSession(client, true);
                  }
                }}
                className="px-4 py-2.5 rounded-xl bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold transition-all shadow-md cursor-pointer"
              >
                Continue Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
